import {
  useAddScheduleRosterAthlete,
  useApplyScheduleSlotFiller,
  useAuth,
  useCancelScheduleRosterBookingAndSuggest,
  useCreateScheduleAthleteAvailability,
  useCreateScheduleCoachAvailability,
  useDeleteScheduleAthleteAvailability,
  useDeleteScheduleCoachAvailability,
  useGenerateSmartScheduleSuggestions,
  useRecordManualBookingPayment,
  useScheduleAthleteAvailability,
  useScheduleCoachAvailability,
  useScheduleDashboard,
  useScheduleOccurrenceDetail,
  useScheduleSlotFillers,
  useTenantBootstrap,
  useTenantDirectory,
  useTenantWorkDashboard,
  useUpdateScheduleAthleteAvailability,
  useUpdateScheduleCoachAvailability,
  useUpdateScheduleMakeupCredit,
  useUpdateScheduleRosterBooking,
  useUpdateTenantWorkRequest,
  useUseBookingPackageCredit,
} from '@tark/shared/hooks';
import { Card, CardContent, CardHeader, CardTitle, Button, Input } from '@tark/shared/components';
import {
  Calendar,
  Clock3,
  Loader2,
  MapPin,
  Radio,
  ShieldCheck,
  UserCircle2,
  Users,
  ArrowRight,
  Building2,
  Sparkles,
  ClipboardList,
  Mail,
  Phone,
  ListChecks,
} from 'lucide-react';
import { FormEvent, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cancelScheduleOccurrence, cn, createScheduleOccurrence, updateScheduleOccurrence } from '@tark/shared/lib';
import CoachPageHeader from '../components/CoachPageHeader';
import { CoachBookableOfferingsPanel } from '../components/schedule/CoachBookableOfferingsPanel';
import type {
  ScheduleDashboardProgram,
  ScheduleDashboardOccurrence,
  TenantAthleteAvailability,
  TenantAthleteAvailabilityPayload,
  TenantCoachAvailability,
  TenantCoachAvailabilityPayload,
  TenantProgram,
  TenantSessionAthleteCandidate,
  TenantSessionRosterEntry,
  TenantSlotFillerSuggestion,
  TenantSmartSchedulePayload,
  TenantSmartScheduleSuggestion,
} from '@tark/shared/types';

const actionRoutes: Record<string, string> = {
  profile: '/profile',
  play: '/play',
  squads: '/squads',
  chats: '/squads',
  buzz: '/buzz',
  pros: '/pros',
  directory: '/directory',
  schedule: '/schedule',
  access: '/tenant-access',
  family: '/family',
};

const actionIcons: Record<string, React.ReactNode> = {
  profile: <UserCircle2 className="h-5 w-5" />,
  play: <Sparkles className="h-5 w-5" />,
  squads: <Users className="h-5 w-5" />,
  chats: <Users className="h-5 w-5" />,
  buzz: <Radio className="h-5 w-5" />,
  pros: <Sparkles className="h-5 w-5" />,
  directory: <Users className="h-5 w-5" />,
  schedule: <Calendar className="h-5 w-5" />,
  access: <ShieldCheck className="h-5 w-5" />,
  family: <Users className="h-5 w-5" />,
};

const rosterAddStatuses = [
  { value: 'BOOKED', label: 'Book' },
  { value: 'REQUESTED', label: 'Request' },
  { value: 'TRIAL_REQUESTED', label: 'Trial' },
  { value: 'WAITLISTED', label: 'Waitlist' },
];

const availabilityDayOptions = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 7, label: 'Sun' },
];

type CoachAvailabilityFormState = {
  availabilityId: number | null;
  entryType: string;
  recurrenceType: string;
  dayOfWeek: string;
  startTimeLocal: string;
  endTimeLocal: string;
  startsAt: string;
  endsAt: string;
  title: string;
  note: string;
};

type StudentAvailabilityFormState = CoachAvailabilityFormState & {
  athleteId: string;
};

type SmartScheduleFormState = {
  programId: string;
  durationMinutes: string;
  searchDays: string;
  capacity: string;
  locationLabel: string;
};

type ScheduleViewMode = 'list' | 'week';

function formatDateRange(startsAt: string, endsAt: string): string {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  const day = start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const startTime = start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const endTime = end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day} • ${startTime} - ${endTime}`;
}

function formatShortDateTime(value?: string | null): string {
  if (!value) {
    return 'Not scheduled';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Not scheduled';
  }

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function toDateTimeLocalValue(date: Date): string {
  const tzOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
}

function defaultOccurrenceForm() {
  const start = new Date();
  start.setMinutes(0, 0, 0);
  start.setHours(start.getHours() + 24);
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  return {
    programId: '',
    title: '',
    startsAt: toDateTimeLocalValue(start),
    endsAt: toDateTimeLocalValue(end),
    locationLabel: '',
    capacity: '',
  };
}

function defaultCoachAvailabilityForm(): CoachAvailabilityFormState {
  const blockStart = new Date();
  blockStart.setMinutes(0, 0, 0);
  blockStart.setHours(blockStart.getHours() + 24);
  const blockEnd = new Date(blockStart.getTime() + 60 * 60 * 1000);

  return {
    availabilityId: null,
    entryType: 'AVAILABLE',
    recurrenceType: 'WEEKLY',
    dayOfWeek: '1',
    startTimeLocal: '16:00',
    endTimeLocal: '19:00',
    startsAt: toDateTimeLocalValue(blockStart),
    endsAt: toDateTimeLocalValue(blockEnd),
    title: '',
    note: '',
  };
}

function defaultStudentAvailabilityForm(athleteId: string = ''): StudentAvailabilityFormState {
  return {
    ...defaultCoachAvailabilityForm(),
    athleteId,
  };
}

function defaultSmartScheduleForm(program?: ScheduleDashboardProgram | TenantProgram | null, timezoneCapacity?: number | null): SmartScheduleFormState {
  return {
    programId: program?.programId ? String(program.programId) : '',
    durationMinutes: '60',
    searchDays: '14',
    capacity: timezoneCapacity != null && timezoneCapacity > 0 ? String(timezoneCapacity) : '',
    locationLabel: '',
  };
}

function toTimeInputValue(value?: string | null): string {
  return value ? value.slice(0, 5) : '';
}

function formatAvailabilityWindow(entry: TenantCoachAvailability | TenantAthleteAvailability): string {
  if ((entry.recurrenceType || '').toUpperCase() === 'WEEKLY') {
    const day = availabilityDayOptions.find((option) => option.value === entry.dayOfWeek)?.label || 'Weekly';
    return `${day} ${toTimeInputValue(entry.startTimeLocal)}-${toTimeInputValue(entry.endTimeLocal)}`;
  }
  return `${formatShortDateTime(entry.startsAt)} - ${formatShortDateTime(entry.endsAt)}`;
}

function availabilityTone(entryType?: string | null): string {
  return (entryType || '').toUpperCase() === 'BLOCKED'
    ? 'border-amber-200 bg-amber-50 text-amber-700'
    : 'border-primary/20 bg-primary/10 text-primary';
}

function buildAvailabilityPayload(
  formState: CoachAvailabilityFormState,
  timezone?: string | null
): TenantCoachAvailabilityPayload {
  const recurrenceType = formState.recurrenceType;
  const base = {
    entryType: formState.entryType,
    recurrenceType,
    timezone: timezone || undefined,
    title: formState.title.trim() || undefined,
    note: formState.note.trim() || undefined,
  };

  if (recurrenceType === 'WEEKLY') {
    return {
      ...base,
      dayOfWeek: Number(formState.dayOfWeek),
      startTimeLocal: formState.startTimeLocal,
      endTimeLocal: formState.endTimeLocal,
    };
  }

  return {
    ...base,
    startsAt: new Date(formState.startsAt).toISOString(),
    endsAt: new Date(formState.endsAt).toISOString(),
  };
}

function buildStudentAvailabilityPayload(
  formState: StudentAvailabilityFormState,
  timezone?: string | null
): TenantAthleteAvailabilityPayload {
  const base = buildAvailabilityPayload(formState, timezone);
  return {
    ...base,
    athleteId: Number(formState.athleteId),
    sourceType: 'COACH',
  };
}

function buildSmartSchedulePayload(
  formState: SmartScheduleFormState,
  timezone?: string | null
): TenantSmartSchedulePayload {
  return {
    programId: formState.programId ? Number(formState.programId) : undefined,
    durationMinutes: formState.durationMinutes ? Number(formState.durationMinutes) : undefined,
    searchDays: formState.searchDays ? Number(formState.searchDays) : undefined,
    capacity: formState.capacity ? Number(formState.capacity) : undefined,
    locationLabel: formState.locationLabel.trim() || undefined,
    timezone: timezone || undefined,
  };
}

function formatRoleLabel(roleCode?: string | null): string {
  if (!roleCode) {
    return 'Member';
  }

  return roleCode
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatScheduleCodeLabel(value?: string | null): string {
  if (!value) {
    return '';
  }
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function rosterStatusClassName(status?: string | null): string {
  switch ((status || '').toUpperCase()) {
    case 'BOOKED':
      return 'border-primary/20 bg-primary/10 text-primary';
    case 'REQUESTED':
      return 'border-sky-200 bg-sky-50 text-sky-700';
    case 'TRIAL_REQUESTED':
      return 'border-primary/20 bg-primary/10 text-primary';
    case 'WAITLISTED':
      return 'border-amber-200 bg-amber-50 text-amber-700';
    case 'CANCELLED':
      return 'border-zinc-200 bg-zinc-50 text-zinc-500';
    default:
      return 'border-border bg-surface-elevated/40 text-text-muted';
  }
}

function attendanceStatusClassName(status?: string | null): string {
  switch ((status || '').toUpperCase()) {
    case 'PRESENT':
      return 'border-primary/20 bg-primary/10 text-primary';
    case 'LATE':
      return 'border-amber-200 bg-amber-50 text-amber-700';
    case 'ABSENT':
      return 'border-red-200 bg-red-50 text-red-700';
    case 'EXCUSED':
      return 'border-sky-200 bg-sky-50 text-sky-700';
    case 'CANCELLED':
      return 'border-zinc-200 bg-zinc-50 text-zinc-500';
    default:
      return 'border-border bg-surface-elevated/40 text-text-subtle';
  }
}

function paymentStatusClassName(status?: string | null): string {
  switch ((status || '').toUpperCase()) {
    case 'PAID':
    case 'PACKAGE':
      return 'border-primary/20 bg-primary/10 text-primary';
    case 'WAIVED':
      return 'border-sky-200 bg-sky-50 text-sky-700';
    case 'REFUNDED':
      return 'border-zinc-200 bg-zinc-50 text-zinc-600';
    default:
      return 'border-amber-200 bg-amber-50 text-amber-700';
  }
}

function makeupStatusClassName(status?: string | null): string {
  switch ((status || '').toUpperCase()) {
    case 'OPEN':
      return 'border-amber-200 bg-amber-50 text-amber-700';
    case 'SCHEDULED':
      return 'border-sky-200 bg-sky-50 text-sky-700';
    case 'USED':
      return 'border-primary/20 bg-primary/10 text-primary';
    case 'WAIVED':
      return 'border-zinc-200 bg-zinc-50 text-zinc-500';
    default:
      return 'border-border bg-surface-elevated/40 text-text-subtle';
  }
}

function makeupReasonLabel(entry: TenantSessionRosterEntry): string {
  if (entry.makeupReasonCode) {
    return formatScheduleCodeLabel(entry.makeupReasonCode);
  }
  if ((entry.attendanceStatus || '').toUpperCase() === 'ABSENT') {
    return 'Absent';
  }
  if ((entry.attendanceStatus || '').toUpperCase() === 'EXCUSED') {
    return 'Excused';
  }
  if ((entry.bookingStatus || '').toUpperCase() === 'CANCELLED') {
    return 'Booking cancelled';
  }
  return 'Missed session';
}

function slotFillerSourceLabel(sourceType?: string | null): string {
  switch ((sourceType || '').toUpperCase()) {
    case 'WAITLIST':
      return 'Waitlist';
    case 'REQUEST':
      return 'Request';
    case 'TRIAL_REQUEST':
      return 'Trial';
    case 'MAKEUP_CREDIT':
      return 'Makeup';
    case 'AVAILABLE_STUDENT':
      return 'Available';
    default:
      return 'Option';
  }
}

function slotFillerSourceClassName(sourceType?: string | null): string {
  switch ((sourceType || '').toUpperCase()) {
    case 'WAITLIST':
    case 'REQUEST':
    case 'TRIAL_REQUEST':
      return 'border-primary/20 bg-primary/10 text-primary';
    case 'MAKEUP_CREDIT':
      return 'border-amber-200 bg-amber-50 text-amber-700';
    default:
      return 'border-border bg-surface-elevated/40 text-text-muted';
  }
}

function slotFillerMeta(suggestion: TenantSlotFillerSuggestion): string {
  return [
    suggestion.reason,
    suggestion.guardianDisplayName ? `Family: ${suggestion.guardianDisplayName}` : null,
    suggestion.sourceOccurrenceTitle ? `From ${suggestion.sourceOccurrenceTitle}` : null,
  ]
    .filter(Boolean)
    .join(' • ');
}

function candidateMeta(candidate: TenantSessionAthleteCandidate): string {
  return [
    candidate.groupLabel,
    formatScheduleCodeLabel(candidate.levelCode),
    formatScheduleCodeLabel(candidate.sportCode),
    candidate.primaryGuardianDisplayName ? `Guardian: ${candidate.primaryGuardianDisplayName}` : null,
  ]
    .filter(Boolean)
    .join(' • ');
}

function rosterEntryMeta(entry: TenantSessionRosterEntry): string {
  return [
    entry.guardianDisplayName ? `Guardian: ${entry.guardianDisplayName}` : null,
    entry.paymentStatus ? `Payment: ${formatScheduleCodeLabel(entry.paymentStatus)}` : null,
    entry.checkedInAt ? `Checked in ${formatShortDateTime(entry.checkedInAt)}` : null,
    entry.bookedAt ? `Added ${formatShortDateTime(entry.bookedAt)}` : null,
  ]
    .filter(Boolean)
    .join(' • ');
}

function resolveOrdersWorkspaceCopy(
  tenantArchetype?: string | null,
  directoryLabel: string = 'Clients',
  workTabLabel: string = 'Orders/Requests'
) {
  switch ((tenantArchetype || '').toUpperCase()) {
    case 'COMMERCE':
      return {
        workflowTitle: 'Seller workflow',
        workflowDescription: `Keep ${workTabLabel.toLowerCase()} official, use Chats for buyer follow-up, and keep ${directoryLabel.toLowerCase()} private to the workspace.`,
        queueTitle: 'Buyer requests',
        queueDescription: 'Every inquiry, order follow-up, and handoff stays here once a buyer comes in through your listing or private workspace.',
        emptyDescription: 'Once buyers contact the linked seller listing, the newest requests and order follow-up will show here.',
        listingTitle: 'Linked seller listing',
        listingDescription: 'Public discovery happens through this listing. Private order handling and follow-through stay inside the workspace.',
        guidanceTitle: 'Seller guidance',
        guidancePoints: [
          `${workTabLabel} is the live queue for inbound demand, order follow-up, and completion status.`,
          'Use Chats for customer updates, fulfillment questions, and internal teammate coordination.',
          `Keep ${directoryLabel.toLowerCase()} private to the tenant, and keep promotional visibility in PROs or BUZZ instead of the work queue.`,
        ],
        notePlaceholder: 'Add a quick order note for this request',
      };
    case 'SERVICE_PROVIDER':
      return {
        workflowTitle: 'Stringer workflow',
        workflowDescription: `Keep ${workTabLabel.toLowerCase()} official, use Chats for pickup or timing follow-up, and manage ${directoryLabel.toLowerCase()} privately inside the workspace.`,
        queueTitle: 'Service requests',
        queueDescription: 'This board is for incoming stringing or service demand once a player, parent, or club reaches out through your listing.',
        emptyDescription: 'Once players or clubs contact the linked service listing, the newest requests will show here.',
        listingTitle: 'Linked PRO listing',
        listingDescription: 'Your public listing brings in discovery. The actual service queue, notes, and client follow-through stay private here.',
        guidanceTitle: 'Stringer guidance',
        guidancePoints: [
          `${workTabLabel} is the active service queue for new work, turnaround, and completion tracking.`,
          'Use Chats for pickup timing, clarification, and repeat-client follow-up without losing the operational thread.',
          `Keep ${directoryLabel.toLowerCase()} private to the tenant, and keep promotion or discovery in PROs or BUZZ instead of the service queue.`,
        ],
        notePlaceholder: 'Add a quick service note for this request',
      };
    default:
      return {
        workflowTitle: 'Orders and requests workflow',
        workflowDescription: `Use ${workTabLabel.toLowerCase()} as the operational queue, then jump into Chats or ${directoryLabel} when the work needs follow-up.`,
        queueTitle: 'Recent requests',
        queueDescription: 'Inbound demand and private follow-up stay here once someone contacts the linked listing or workspace.',
        emptyDescription: 'Once users contact the linked PRO listing, the newest requests will show here.',
        listingTitle: 'Linked listing',
        listingDescription: 'Public discovery happens through the listing. Private work stays inside this tenant workspace.',
        guidanceTitle: 'Orders/Requests guidance',
        guidancePoints: [
          `Use ${workTabLabel} as the active queue for inbound demand and next steps.`,
          `Use Chats for direct follow-up and keep ${directoryLabel.toLowerCase()} private to the tenant workspace.`,
          'Keep public promotion in PROs or BUZZ instead of mixing it into operational follow-up.',
        ],
        notePlaceholder: 'Add a quick note for this request',
      };
  }
}

function CoachScheduleEmptySetup({
  hasPrograms,
  hasStudents,
  onOpenCatalog,
  onCreateSession,
  onOpenAccess,
  onOpenDirectory,
}: {
  hasPrograms: boolean;
  hasStudents: boolean;
  onOpenCatalog: () => void;
  onCreateSession: () => void;
  onOpenAccess: () => void;
  onOpenDirectory: () => void;
}) {
  const primaryTitle = !hasPrograms ? 'Create classes first' : hasStudents ? 'Create the first session' : 'Add students first';
  const primaryDescription = !hasPrograms
    ? 'Build the season, class options, fees, and slots before creating sessions.'
    : hasStudents
      ? 'Schedule the first lesson, then open roster when students are ready.'
      : 'Create a family join code. Students appear after guardians join and add athletes.';
  const primaryAction = !hasPrograms
    ? { label: 'Open Classes', onClick: onOpenCatalog, icon: <Building2 className="h-4 w-4" /> }
    : hasStudents
      ? { label: 'Create Session', onClick: onCreateSession, icon: null }
      : { label: 'Create Join Code', onClick: onOpenAccess, icon: <ShieldCheck className="h-4 w-4" /> };

  return (
    <div className="rounded-2xl border border-dashed border-border bg-surface-elevated/40 p-5">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">First session</p>
          <p className="mt-2 text-xl font-semibold text-text">{primaryTitle}</p>
          <p className="mt-2 max-w-xl text-sm text-text-muted">
            {primaryDescription}
          </p>
        </div>
        <div className="flex flex-wrap gap-3 md:justify-end">
          <Button onClick={primaryAction.onClick} leftIcon={primaryAction.icon}>
            {primaryAction.label}
          </Button>
          <Button variant="outline" onClick={onOpenDirectory} leftIcon={<Users className="h-4 w-4" />}>
            Students
          </Button>
        </div>
      </div>
    </div>
  );
}

function buildCurrentWeekDays(): Date[] {
  const start = new Date();
  const day = start.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  start.setDate(start.getDate() + mondayOffset);
  start.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return date;
  });
}

function isSameLocalDay(left: Date, right: Date): boolean {
  return left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate();
}

function formatWeekDay(date: Date): string {
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatWeekTime(startsAt: string, endsAt: string): string {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return 'Time TBD';
  }
  return `${start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} - ${end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
}

function ScheduleWeekGrid({
  occurrences,
  canManageSchedule,
  mode,
  selectedRosterOccurrenceId,
  cancellingOccurrenceId,
  onToggleRoster,
  onEdit,
  onCancel,
  onOpenChat,
}: {
  occurrences: ScheduleDashboardOccurrence[];
  canManageSchedule: boolean;
  mode: string;
  selectedRosterOccurrenceId: number | null;
  cancellingOccurrenceId: number | null;
  onToggleRoster: (occurrenceId: number) => void;
  onEdit: (occurrence: ScheduleDashboardOccurrence) => void;
  onCancel: (occurrenceId: number) => void;
  onOpenChat: (chatSquadId: number) => void;
}) {
  const days = buildCurrentWeekDays();
  const today = new Date();

  return (
    <div className="grid gap-3 lg:grid-cols-7">
      {days.map((day) => {
        const dayOccurrences = occurrences.filter((occurrence) => isSameLocalDay(new Date(occurrence.startsAt), day));
        return (
          <div
            key={day.toISOString()}
            className={cn(
              'min-h-[180px] rounded-2xl border bg-surface-elevated/35 p-3',
              isSameLocalDay(day, today) ? 'border-primary/35' : 'border-border'
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-text-subtle">{formatWeekDay(day)}</p>
              {isSameLocalDay(day, today) ? (
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-primary">
                  Today
                </span>
              ) : null}
            </div>

            <div className="mt-3 space-y-2">
              {dayOccurrences.length > 0 ? (
                dayOccurrences.map((occurrence) => (
                  <div key={occurrence.occurrenceId} className="rounded-xl border border-border bg-background p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-text">{occurrence.title}</p>
                        <p className="mt-1 truncate text-xs text-text-muted">{occurrence.programName || 'Tenant session'}</p>
                      </div>
                      <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-primary">
                        {occurrence.occurrenceStatus || 'Scheduled'}
                      </span>
                    </div>
                    <div className="mt-3 space-y-1.5 text-xs text-text-muted">
                      <div className="flex items-center gap-1.5">
                        <Clock3 className="h-3.5 w-3.5 text-primary" />
                        <span>{formatWeekTime(occurrence.startsAt, occurrence.endsAt)}</span>
                      </div>
                      {occurrence.locationLabel ? (
                        <div className="flex items-center gap-1.5">
                          <MapPin className="h-3.5 w-3.5 text-primary" />
                          <span className="truncate">{occurrence.locationLabel}</span>
                        </div>
                      ) : null}
                      {occurrence.coachName ? (
                        <div className="flex items-center gap-1.5">
                          <UserCircle2 className="h-3.5 w-3.5 text-primary" />
                          <span className="truncate">{occurrence.coachName}</span>
                        </div>
                      ) : null}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {canManageSchedule && mode === 'TENANT' ? (
                        <Button
                          size="xs"
                          variant={selectedRosterOccurrenceId === occurrence.occurrenceId ? 'secondary' : 'outline'}
                          onClick={() => onToggleRoster(occurrence.occurrenceId)}
                        >
                          Roster
                        </Button>
                      ) : null}
                      {canManageSchedule && mode === 'TENANT' && (occurrence.occurrenceStatus || '').toUpperCase() !== 'CANCELLED' ? (
                        <Button size="xs" variant="ghost" onClick={() => onEdit(occurrence)}>
                          Edit
                        </Button>
                      ) : null}
                      {occurrence.chatSquadId ? (
                        <Button size="xs" variant="ghost" onClick={() => onOpenChat(occurrence.chatSquadId!)}>
                          Chat
                        </Button>
                      ) : null}
                      {canManageSchedule && mode === 'TENANT' && (occurrence.occurrenceStatus || '').toUpperCase() !== 'CANCELLED' ? (
                        <Button
                          size="xs"
                          variant="outline"
                          isLoading={cancellingOccurrenceId === occurrence.occurrenceId}
                          onClick={() => onCancel(occurrence.occurrenceId)}
                        >
                          Cancel
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ))
              ) : (
                <p className="rounded-xl border border-dashed border-border bg-background/70 px-3 py-4 text-center text-xs text-text-subtle">
                  No sessions
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CoachAvailabilityPanel({
  enabled,
  canManageSchedule,
  timezone,
  onAvailabilityChanged,
}: {
  enabled: boolean;
  canManageSchedule: boolean;
  timezone?: string | null;
  onAvailabilityChanged: () => Promise<unknown>;
}) {
  const { data: availability = [], isLoading, isError, refetch } = useScheduleCoachAvailability(enabled);
  const createAvailability = useCreateScheduleCoachAvailability();
  const updateAvailability = useUpdateScheduleCoachAvailability();
  const deleteAvailability = useDeleteScheduleCoachAvailability();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formState, setFormState] = useState<CoachAvailabilityFormState>(() => defaultCoachAvailabilityForm());
  const [formError, setFormError] = useState<string | null>(null);

  const availableWindows = availability.filter((entry) => (entry.entryType || '').toUpperCase() === 'AVAILABLE');
  const blockedWindows = availability.filter((entry) => (entry.entryType || '').toUpperCase() === 'BLOCKED');
  const isSaving = createAvailability.isPending || updateAvailability.isPending;

  const resetAvailabilityForm = () => {
    setFormState(defaultCoachAvailabilityForm());
    setFormError(null);
  };

  const openEditAvailability = (entry: TenantCoachAvailability) => {
    const nextState = defaultCoachAvailabilityForm();
    setFormState({
      ...nextState,
      availabilityId: entry.availabilityId,
      entryType: (entry.entryType || 'AVAILABLE').toUpperCase(),
      recurrenceType: (entry.recurrenceType || 'WEEKLY').toUpperCase(),
      dayOfWeek: String(entry.dayOfWeek || 1),
      startTimeLocal: toTimeInputValue(entry.startTimeLocal) || nextState.startTimeLocal,
      endTimeLocal: toTimeInputValue(entry.endTimeLocal) || nextState.endTimeLocal,
      startsAt: entry.startsAt ? toDateTimeLocalValue(new Date(entry.startsAt)) : nextState.startsAt,
      endsAt: entry.endsAt ? toDateTimeLocalValue(new Date(entry.endsAt)) : nextState.endsAt,
      title: entry.title || '',
      note: entry.note || '',
    });
    setFormError(null);
    setIsFormOpen(true);
  };

  const refreshAvailability = async () => {
    await Promise.all([refetch(), onAvailabilityChanged()]);
  };

  const handleAvailabilitySubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (formState.recurrenceType === 'WEEKLY' && (!formState.startTimeLocal || !formState.endTimeLocal)) {
      setFormError('Add start and end time.');
      return;
    }

    if (formState.recurrenceType === 'ONE_TIME' && (!formState.startsAt || !formState.endsAt)) {
      setFormError('Add start and end date/time.');
      return;
    }

    try {
      setFormError(null);
      const payload = buildAvailabilityPayload(formState, timezone);
      if (formState.availabilityId) {
        await updateAvailability.mutateAsync({ availabilityId: formState.availabilityId, payload });
      } else {
        await createAvailability.mutateAsync(payload);
      }
      await refreshAvailability();
      resetAvailabilityForm();
      setIsFormOpen(false);
    } catch (error) {
      console.error('Failed to save coach availability', error);
      setFormError('Could not save availability. Check the details and try again.');
    }
  };

  const handleDeleteAvailability = async (entry: TenantCoachAvailability) => {
    if (!window.confirm(`Remove "${entry.title || formatAvailabilityWindow(entry)}"?`)) {
      return;
    }

    try {
      await deleteAvailability.mutateAsync(entry.availabilityId);
      await refreshAvailability();
      if (formState.availabilityId === entry.availabilityId) {
        resetAvailabilityForm();
        setIsFormOpen(false);
      }
    } catch (error) {
      console.error('Failed to remove coach availability', error);
      window.alert('Could not remove this availability window right now.');
    }
  };

  return (
    <Card id="coach-availability" variant="default">
      <CardHeader>
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Clock3 className="h-5 w-5 text-primary" />
              Availability
            </CardTitle>
            <p className="mt-1 text-sm text-text-muted">
              Coaching windows and blocked time for bookings and schedule planning.
            </p>
          </div>
          {canManageSchedule ? (
            <Button
              size="sm"
              variant={isFormOpen ? 'secondary' : 'outline'}
              onClick={() => {
                if (isFormOpen) {
                  setIsFormOpen(false);
                  resetAvailabilityForm();
                  return;
                }
                setIsFormOpen(true);
              }}
            >
              {isFormOpen ? 'Close' : 'Add Window'}
            </Button>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="rounded-2xl border border-border bg-surface-elevated/20 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle">Available</p>
            <p className="mt-1 text-2xl font-semibold tracking-tight text-text">{availableWindows.length}</p>
          </div>
          <div className="rounded-2xl border border-border bg-surface-elevated/20 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle">Blocked</p>
            <p className="mt-1 text-2xl font-semibold tracking-tight text-text">{blockedWindows.length}</p>
          </div>
          <div className="rounded-2xl border border-border bg-surface-elevated/20 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle">Timezone</p>
            <p className="mt-2 truncate text-sm font-semibold text-text">{timezone || 'America/Chicago'}</p>
          </div>
        </div>

        {canManageSchedule && isFormOpen ? (
          <form onSubmit={handleAvailabilitySubmit} className="rounded-2xl border border-border bg-surface-elevated/50 p-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-text">Type</label>
                <select
                  className="w-full rounded-lg border border-border bg-surface px-3.5 py-2.5 text-sm text-text focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                  value={formState.entryType}
                  onChange={(event) => setFormState((current) => ({ ...current, entryType: event.target.value }))}
                >
                  <option value="AVAILABLE">Available</option>
                  <option value="BLOCKED">Blocked</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-text">Repeat</label>
                <select
                  className="w-full rounded-lg border border-border bg-surface px-3.5 py-2.5 text-sm text-text focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                  value={formState.recurrenceType}
                  onChange={(event) => setFormState((current) => ({ ...current, recurrenceType: event.target.value }))}
                >
                  <option value="WEEKLY">Weekly</option>
                  <option value="ONE_TIME">One time</option>
                </select>
              </div>

              {formState.recurrenceType === 'WEEKLY' ? (
                <>
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-text">Day</label>
                    <select
                      className="w-full rounded-lg border border-border bg-surface px-3.5 py-2.5 text-sm text-text focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                      value={formState.dayOfWeek}
                      onChange={(event) => setFormState((current) => ({ ...current, dayOfWeek: event.target.value }))}
                    >
                      {availabilityDayOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="Start"
                      type="time"
                      value={formState.startTimeLocal}
                      onChange={(event) => setFormState((current) => ({ ...current, startTimeLocal: event.target.value }))}
                    />
                    <Input
                      label="End"
                      type="time"
                      value={formState.endTimeLocal}
                      onChange={(event) => setFormState((current) => ({ ...current, endTimeLocal: event.target.value }))}
                    />
                  </div>
                </>
              ) : (
                <>
                  <Input
                    label="Starts"
                    type="datetime-local"
                    value={formState.startsAt}
                    onChange={(event) => setFormState((current) => ({ ...current, startsAt: event.target.value }))}
                  />
                  <Input
                    label="Ends"
                    type="datetime-local"
                    value={formState.endsAt}
                    onChange={(event) => setFormState((current) => ({ ...current, endsAt: event.target.value }))}
                  />
                </>
              )}

              <Input
                label="Label"
                placeholder={formState.entryType === 'BLOCKED' ? 'Tournament travel' : 'Private lessons'}
                value={formState.title}
                onChange={(event) => setFormState((current) => ({ ...current, title: event.target.value }))}
              />
              <Input
                label="Note"
                placeholder="Optional"
                value={formState.note}
                onChange={(event) => setFormState((current) => ({ ...current, note: event.target.value }))}
              />
            </div>
            {formError ? <p className="mt-3 text-sm text-red-400">{formError}</p> : null}
            <div className="mt-4 flex flex-wrap gap-3">
              <Button type="submit" isLoading={isSaving}>
                {formState.availabilityId ? 'Save Window' : 'Add Window'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  resetAvailabilityForm();
                  setIsFormOpen(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : null}

        {isLoading ? (
          <div className="flex items-center gap-3 rounded-2xl border border-border bg-surface-elevated/30 p-4">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            <p className="text-sm text-text-muted">Loading availability...</p>
          </div>
        ) : null}

        {isError ? (
          <div className="flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-700 md:flex-row md:items-center md:justify-between">
            <p className="text-sm font-medium">Availability could not load right now.</p>
            <Button size="sm" variant="outline" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        ) : null}

        {!isLoading && !isError ? (
          availability.length > 0 ? (
            <div className="grid gap-3 md:grid-cols-2">
              {availability.map((entry) => (
                <div key={entry.availabilityId} className="rounded-2xl border border-border bg-surface-elevated/30 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${availabilityTone(entry.entryType)}`}>
                          {formatScheduleCodeLabel(entry.entryType)}
                        </span>
                        <span className="rounded-full border border-border bg-surface px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-text-subtle">
                          {formatScheduleCodeLabel(entry.recurrenceType)}
                        </span>
                      </div>
                      <p className="mt-3 font-semibold text-text">{entry.title || formatAvailabilityWindow(entry)}</p>
                      <p className="mt-1 text-sm text-text-muted">{formatAvailabilityWindow(entry)}</p>
                      {entry.note ? <p className="mt-2 line-clamp-2 text-sm text-text-muted">{entry.note}</p> : null}
                    </div>
                    {canManageSchedule ? (
                      <div className="flex shrink-0 flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => openEditAvailability(entry)}>
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          isLoading={deleteAvailability.isPending && deleteAvailability.variables === entry.availabilityId}
                          onClick={() => handleDeleteAvailability(entry)}
                        >
                          Remove
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-border bg-surface-elevated/20 p-5">
              <p className="font-semibold text-text">No availability yet</p>
            </div>
          )
        ) : null}
      </CardContent>
    </Card>
  );
}

function StudentSmartSchedulePanel({
  enabled,
  canManageSchedule,
  timezone,
  programs,
  onAvailabilityChanged,
  onUseSuggestion,
}: {
  enabled: boolean;
  canManageSchedule: boolean;
  timezone?: string | null;
  programs: ScheduleDashboardProgram[];
  onAvailabilityChanged: () => Promise<unknown>;
  onUseSuggestion: (suggestion: TenantSmartScheduleSuggestion, form: SmartScheduleFormState) => void;
}) {
  const { data, isLoading, isError, refetch } = useScheduleAthleteAvailability(enabled);
  const createAvailability = useCreateScheduleAthleteAvailability();
  const updateAvailability = useUpdateScheduleAthleteAvailability();
  const deleteAvailability = useDeleteScheduleAthleteAvailability();
  const generateSuggestions = useGenerateSmartScheduleSuggestions();
  const athletes = data?.athletes ?? [];
  const availability = data?.items ?? [];
  const firstAthleteId = athletes[0]?.athleteId ? String(athletes[0].athleteId) : '';
  const firstProgram = programs[0] ?? null;
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formState, setFormState] = useState<StudentAvailabilityFormState>(() => defaultStudentAvailabilityForm());
  const [smartForm, setSmartForm] = useState<SmartScheduleFormState>(() =>
    defaultSmartScheduleForm(firstProgram, firstProgram?.defaultCapacity)
  );
  const [formError, setFormError] = useState<string | null>(null);
  const [smartError, setSmartError] = useState<string | null>(null);

  const availableWindows = availability.filter((entry) => (entry.entryType || '').toUpperCase() === 'AVAILABLE');
  const blockedWindows = availability.filter((entry) => (entry.entryType || '').toUpperCase() === 'BLOCKED');
  const selectedProgram = smartForm.programId
    ? programs.find((program) => String(program.programId) === smartForm.programId) ?? null
    : null;
  const isSaving = createAvailability.isPending || updateAvailability.isPending;

  const resetStudentForm = (athleteId: string = firstAthleteId) => {
    setFormState(defaultStudentAvailabilityForm(athleteId));
    setFormError(null);
  };

  const openStudentAvailabilityForm = () => {
    resetStudentForm(firstAthleteId);
    setIsFormOpen(true);
  };

  const openEditStudentAvailability = (entry: TenantAthleteAvailability) => {
    const nextState = defaultStudentAvailabilityForm(String(entry.athleteId));
    setFormState({
      ...nextState,
      availabilityId: entry.availabilityId,
      athleteId: String(entry.athleteId),
      entryType: (entry.entryType || 'AVAILABLE').toUpperCase(),
      recurrenceType: (entry.recurrenceType || 'WEEKLY').toUpperCase(),
      dayOfWeek: String(entry.dayOfWeek || 1),
      startTimeLocal: toTimeInputValue(entry.startTimeLocal) || nextState.startTimeLocal,
      endTimeLocal: toTimeInputValue(entry.endTimeLocal) || nextState.endTimeLocal,
      startsAt: entry.startsAt ? toDateTimeLocalValue(new Date(entry.startsAt)) : nextState.startsAt,
      endsAt: entry.endsAt ? toDateTimeLocalValue(new Date(entry.endsAt)) : nextState.endsAt,
      title: entry.title || '',
      note: entry.note || '',
    });
    setFormError(null);
    setIsFormOpen(true);
  };

  const refreshAvailability = async () => {
    await Promise.all([refetch(), onAvailabilityChanged()]);
  };

  const handleStudentAvailabilitySubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!formState.athleteId) {
      setFormError('Choose a student.');
      return;
    }
    if (formState.recurrenceType === 'WEEKLY' && (!formState.startTimeLocal || !formState.endTimeLocal)) {
      setFormError('Add start and end time.');
      return;
    }
    if (formState.recurrenceType === 'ONE_TIME' && (!formState.startsAt || !formState.endsAt)) {
      setFormError('Add start and end date/time.');
      return;
    }

    try {
      setFormError(null);
      const payload = buildStudentAvailabilityPayload(formState, timezone);
      if (formState.availabilityId) {
        await updateAvailability.mutateAsync({ availabilityId: formState.availabilityId, payload });
      } else {
        await createAvailability.mutateAsync(payload);
      }
      await refreshAvailability();
      resetStudentForm();
      setIsFormOpen(false);
    } catch (error) {
      console.error('Failed to save student availability', error);
      setFormError('Could not save student availability. Check the details and try again.');
    }
  };

  const handleDeleteStudentAvailability = async (entry: TenantAthleteAvailability) => {
    if (!window.confirm(`Remove "${entry.athleteDisplayName} - ${entry.title || formatAvailabilityWindow(entry)}"?`)) {
      return;
    }

    try {
      await deleteAvailability.mutateAsync(entry.availabilityId);
      await refreshAvailability();
      if (formState.availabilityId === entry.availabilityId) {
        resetStudentForm();
        setIsFormOpen(false);
      }
    } catch (error) {
      console.error('Failed to remove student availability', error);
      window.alert('Could not remove this student availability window right now.');
    }
  };

  const handleGenerateSuggestions = async () => {
    try {
      setSmartError(null);
      await generateSuggestions.mutateAsync(buildSmartSchedulePayload(smartForm, timezone));
    } catch (error) {
      console.error('Failed to generate smart schedule suggestions', error);
      setSmartError('Could not generate options yet. Make sure coach and student availability are saved.');
    }
  };

  const updateSmartProgram = (programId: string) => {
    const program = programs.find((item) => String(item.programId) === programId) ?? null;
    setSmartForm((current) => ({
      ...current,
      programId,
      capacity: current.capacity || (program?.defaultCapacity != null ? String(program.defaultCapacity) : ''),
    }));
  };

  return (
    <Card id="student-availability" variant="default">
      <CardHeader>
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Smart schedule
            </CardTitle>
            <p className="mt-1 text-sm text-text-muted">
              Student preferences feed deterministic schedule options. Nothing is created until you choose a time.
            </p>
          </div>
          {canManageSchedule ? (
            <Button
              size="sm"
              variant={isFormOpen ? 'secondary' : 'outline'}
              onClick={() => {
                if (isFormOpen) {
                  setIsFormOpen(false);
                  resetStudentForm();
                  return;
                }
                openStudentAvailabilityForm();
              }}
              disabled={athletes.length === 0}
            >
              {isFormOpen ? 'Close' : 'Add Preference'}
            </Button>
          ) : null}
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        <div className="grid gap-3 sm:grid-cols-4">
          <div className="rounded-2xl border border-border bg-surface-elevated/20 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle">Students</p>
            <p className="mt-1 text-2xl font-semibold tracking-tight text-text">{athletes.length}</p>
          </div>
          <div className="rounded-2xl border border-border bg-surface-elevated/20 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle">Ready</p>
            <p className="mt-1 text-2xl font-semibold tracking-tight text-text">
              {athletes.filter((athlete) => athlete.availableWindowCount > 0).length}
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-surface-elevated/20 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle">Available</p>
            <p className="mt-1 text-2xl font-semibold tracking-tight text-text">{availableWindows.length}</p>
          </div>
          <div className="rounded-2xl border border-border bg-surface-elevated/20 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle">Blocked</p>
            <p className="mt-1 text-2xl font-semibold tracking-tight text-text">{blockedWindows.length}</p>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center gap-3 rounded-2xl border border-border bg-surface-elevated/30 p-4">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            <p className="text-sm text-text-muted">Loading student preferences...</p>
          </div>
        ) : null}

        {isError ? (
          <div className="flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-700 md:flex-row md:items-center md:justify-between">
            <p className="text-sm font-medium">Student preferences could not load right now.</p>
            <Button size="sm" variant="outline" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        ) : null}

        {canManageSchedule && isFormOpen ? (
          <form onSubmit={handleStudentAvailabilitySubmit} className="rounded-2xl border border-border bg-surface-elevated/50 p-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-text">Student</label>
                <select
                  className="w-full rounded-lg border border-border bg-surface px-3.5 py-2.5 text-sm text-text focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                  value={formState.athleteId}
                  onChange={(event) => setFormState((current) => ({ ...current, athleteId: event.target.value }))}
                >
                  <option value="">Choose student</option>
                  {athletes.map((athlete) => (
                    <option key={athlete.athleteId} value={athlete.athleteId}>
                      {athlete.displayName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-text">Type</label>
                  <select
                    className="w-full rounded-lg border border-border bg-surface px-3.5 py-2.5 text-sm text-text focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                    value={formState.entryType}
                    onChange={(event) => setFormState((current) => ({ ...current, entryType: event.target.value }))}
                  >
                    <option value="AVAILABLE">Available</option>
                    <option value="BLOCKED">Blocked</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-text">Repeat</label>
                  <select
                    className="w-full rounded-lg border border-border bg-surface px-3.5 py-2.5 text-sm text-text focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                    value={formState.recurrenceType}
                    onChange={(event) => setFormState((current) => ({ ...current, recurrenceType: event.target.value }))}
                  >
                    <option value="WEEKLY">Weekly</option>
                    <option value="ONE_TIME">One time</option>
                  </select>
                </div>
              </div>

              {formState.recurrenceType === 'WEEKLY' ? (
                <>
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-text">Day</label>
                    <select
                      className="w-full rounded-lg border border-border bg-surface px-3.5 py-2.5 text-sm text-text focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                      value={formState.dayOfWeek}
                      onChange={(event) => setFormState((current) => ({ ...current, dayOfWeek: event.target.value }))}
                    >
                      {availabilityDayOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="Start"
                      type="time"
                      value={formState.startTimeLocal}
                      onChange={(event) => setFormState((current) => ({ ...current, startTimeLocal: event.target.value }))}
                    />
                    <Input
                      label="End"
                      type="time"
                      value={formState.endTimeLocal}
                      onChange={(event) => setFormState((current) => ({ ...current, endTimeLocal: event.target.value }))}
                    />
                  </div>
                </>
              ) : (
                <>
                  <Input
                    label="Starts"
                    type="datetime-local"
                    value={formState.startsAt}
                    onChange={(event) => setFormState((current) => ({ ...current, startsAt: event.target.value }))}
                  />
                  <Input
                    label="Ends"
                    type="datetime-local"
                    value={formState.endsAt}
                    onChange={(event) => setFormState((current) => ({ ...current, endsAt: event.target.value }))}
                  />
                </>
              )}

              <Input
                label="Label"
                placeholder={formState.entryType === 'BLOCKED' ? 'Exam week' : 'Weeknight lessons'}
                value={formState.title}
                onChange={(event) => setFormState((current) => ({ ...current, title: event.target.value }))}
              />
              <Input
                label="Note"
                placeholder="Optional"
                value={formState.note}
                onChange={(event) => setFormState((current) => ({ ...current, note: event.target.value }))}
              />
            </div>
            {formError ? <p className="mt-3 text-sm text-red-400">{formError}</p> : null}
            <div className="mt-4 flex flex-wrap gap-3">
              <Button type="submit" isLoading={isSaving}>
                {formState.availabilityId ? 'Save Preference' : 'Add Preference'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  resetStudentForm();
                  setIsFormOpen(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : null}

        {!isLoading && !isError ? (
          availability.length > 0 ? (
            <div className="grid gap-3 md:grid-cols-2">
              {availability.slice(0, 6).map((entry) => (
                <div key={entry.availabilityId} className="rounded-2xl border border-border bg-surface-elevated/30 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${availabilityTone(entry.entryType)}`}>
                          {formatScheduleCodeLabel(entry.entryType)}
                        </span>
                        <span className="rounded-full border border-border bg-surface px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-text-subtle">
                          {entry.athleteDisplayName}
                        </span>
                      </div>
                      <p className="mt-3 font-semibold text-text">{entry.title || formatAvailabilityWindow(entry)}</p>
                      <p className="mt-1 text-sm text-text-muted">{formatAvailabilityWindow(entry)}</p>
                    </div>
                    {canManageSchedule ? (
                      <div className="flex shrink-0 flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => openEditStudentAvailability(entry)}>
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          isLoading={deleteAvailability.isPending && deleteAvailability.variables === entry.availabilityId}
                          onClick={() => handleDeleteStudentAvailability(entry)}
                        >
                          Remove
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-border bg-surface-elevated/20 p-5">
              <p className="font-semibold text-text">No student preferences yet</p>
              <p className="mt-1 text-sm text-text-muted">Add availability for students before generating options.</p>
            </div>
          )
        ) : null}

        <div className="rounded-[24px] border border-primary/15 bg-primary/5 p-4">
          <div className="grid gap-4 md:grid-cols-5">
            <div className="space-y-1.5 md:col-span-2">
              <label className="block text-sm font-medium text-text">Program</label>
              <select
                className="w-full rounded-lg border border-border bg-surface px-3.5 py-2.5 text-sm text-text focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                value={smartForm.programId}
                onChange={(event) => updateSmartProgram(event.target.value)}
              >
                <option value="">Any active student</option>
                {programs.map((program) => (
                  <option key={program.programId} value={program.programId}>
                    {program.name}
                  </option>
                ))}
              </select>
            </div>
            <Input
              label="Minutes"
              type="number"
              min={30}
              max={180}
              step={30}
              value={smartForm.durationMinutes}
              onChange={(event) => setSmartForm((current) => ({ ...current, durationMinutes: event.target.value }))}
            />
            <Input
              label="Days"
              type="number"
              min={1}
              max={28}
              value={smartForm.searchDays}
              onChange={(event) => setSmartForm((current) => ({ ...current, searchDays: event.target.value }))}
            />
            <Input
              label="Capacity"
              type="number"
              min={1}
              placeholder={selectedProgram?.defaultCapacity != null ? String(selectedProgram.defaultCapacity) : '4'}
              value={smartForm.capacity}
              onChange={(event) => setSmartForm((current) => ({ ...current, capacity: event.target.value }))}
            />
            <Input
              label="Location"
              placeholder="Court A"
              value={smartForm.locationLabel}
              onChange={(event) => setSmartForm((current) => ({ ...current, locationLabel: event.target.value }))}
            />
            <div className="flex items-end md:col-span-2">
              <Button
                className="w-full"
                isLoading={generateSuggestions.isPending}
                onClick={handleGenerateSuggestions}
                disabled={athletes.length === 0}
              >
                Generate Options
              </Button>
            </div>
          </div>
          {smartError ? <p className="mt-3 text-sm text-red-400">{smartError}</p> : null}
        </div>

        {generateSuggestions.data ? (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm font-semibold text-text">
                {generateSuggestions.data.suggestions.length} option{generateSuggestions.data.suggestions.length === 1 ? '' : 's'} from {generateSuggestions.data.totalEligibleAthletes} eligible student{generateSuggestions.data.totalEligibleAthletes === 1 ? '' : 's'}
              </p>
              <p className="text-xs uppercase tracking-[0.16em] text-text-subtle">{generateSuggestions.data.timezone}</p>
            </div>
            {generateSuggestions.data.suggestions.length > 0 ? (
              <div className="grid gap-3 lg:grid-cols-2">
                {generateSuggestions.data.suggestions.map((suggestion) => {
                  const matchedAthletes = suggestion.athletes.filter((athlete) => athlete.matchedAvailability);
                  return (
                    <div key={`${suggestion.startsAt}-${suggestion.endsAt}`} className="rounded-2xl border border-border bg-surface p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-text-subtle">{suggestion.dayLabel}</p>
                          <p className="mt-1 text-lg font-semibold text-text">{suggestion.timeLabel}</p>
                          <p className="mt-1 text-sm text-text-muted">
                            {suggestion.matchingAthleteCount}/{suggestion.totalAthleteCount} match
                            {suggestion.capacity ? ` • ${suggestion.openSpots} spots left` : ''}
                          </p>
                        </div>
                        <span className="rounded-full bg-primary px-3 py-1 text-xs font-semibold [color:var(--color-on-primary)]">
                          {suggestion.score}
                        </span>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {matchedAthletes.slice(0, 5).map((athlete) => (
                          <span key={athlete.athleteId} className="rounded-full border border-primary/15 bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                            {athlete.displayName}
                          </span>
                        ))}
                        {matchedAthletes.length > 5 ? (
                          <span className="rounded-full border border-border bg-surface-elevated/40 px-2.5 py-1 text-xs font-medium text-text-muted">
                            +{matchedAthletes.length - 5}
                          </span>
                        ) : null}
                      </div>
                      {suggestion.warnings.length > 0 ? (
                        <p className="mt-3 text-xs text-amber-700">{suggestion.warnings.join(' • ')}</p>
                      ) : null}
                      <div className="mt-4 flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          onClick={() => onUseSuggestion(suggestion, smartForm)}
                          disabled={!smartForm.programId}
                        >
                          Use This Time
                        </Button>
                        {!smartForm.programId ? (
                          <p className="self-center text-xs text-text-subtle">Choose a program to create.</p>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-border bg-surface-elevated/20 p-5">
                <p className="font-semibold text-text">No matching windows yet</p>
                <p className="mt-1 text-sm text-text-muted">Add more student availability or widen the date range.</p>
              </div>
            )}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function SessionRosterPanel({
  occurrenceId,
  canManageSchedule,
  onClose,
}: {
  occurrenceId: number;
  canManageSchedule: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useScheduleOccurrenceDetail(occurrenceId, true);
  const {
    data: slotFillers,
    isFetching: isFetchingSlotFillers,
    refetch: refetchSlotFillers,
  } = useScheduleSlotFillers(occurrenceId, canManageSchedule);
  const addRosterAthlete = useAddScheduleRosterAthlete();
  const updateRosterBooking = useUpdateScheduleRosterBooking();
  const cancelRosterBooking = useCancelScheduleRosterBookingAndSuggest();
  const applySlotFiller = useApplyScheduleSlotFiller();
  const updateMakeupCredit = useUpdateScheduleMakeupCredit();
  const recordManualPayment = useRecordManualBookingPayment();
  const usePackageCredit = useUseBookingPackageCredit();
  const [selectedAthleteId, setSelectedAthleteId] = useState('');
  const [newBookingStatus, setNewBookingStatus] = useState('BOOKED');
  const [formError, setFormError] = useState<string | null>(null);
  const [actionBookingId, setActionBookingId] = useState<number | null>(null);
  const [paymentActionBookingId, setPaymentActionBookingId] = useState<number | null>(null);
  const [makeupActionBookingId, setMakeupActionBookingId] = useState<number | null>(null);
  const [slotFillerActionKey, setSlotFillerActionKey] = useState<string | null>(null);

  const selectedCandidate = data?.availableAthletes.find(
    (candidate) => String(candidate.athleteId) === selectedAthleteId
  );
  const roster = data?.roster ?? [];
  const activeRoster = roster.filter((entry) => (entry.bookingStatus || '').toUpperCase() !== 'CANCELLED');
  const cancelledRoster = roster.filter((entry) => (entry.bookingStatus || '').toUpperCase() === 'CANCELLED');
  const requestQueueCount =
    (data?.rosterSummary.requestedCount ?? 0) + (data?.rosterSummary.trialRequestedCount ?? 0);
  const slotFillerSuggestions = slotFillers?.suggestions ?? [];
  const openSpotCount = slotFillers?.openSpotCount ?? data?.rosterSummary.openSpots ?? 0;

  const handleAddAthlete = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedAthleteId) {
      setFormError('Choose a student.');
      return;
    }

    setFormError(null);
    try {
      await addRosterAthlete.mutateAsync({
        occurrenceId,
        payload: {
          athleteId: Number(selectedAthleteId),
          guardianProfileId: selectedCandidate?.primaryGuardianProfileId ?? null,
          bookingStatus: newBookingStatus,
        },
      });
      setSelectedAthleteId('');
      await refetch();
      await refetchSlotFillers();
    } catch (error) {
      console.error('Failed to add roster athlete', error);
      setFormError('Could not add this student to the roster.');
    }
  };

  const updateBookingStatus = async (entry: TenantSessionRosterEntry, bookingStatus: string) => {
    setActionBookingId(entry.bookingId);
    setFormError(null);
    try {
      await updateRosterBooking.mutateAsync({
        occurrenceId,
        bookingId: entry.bookingId,
        payload: { bookingStatus },
      });
      await refetch();
      await refetchSlotFillers();
    } catch (error) {
      console.error('Failed to update roster booking', error);
      setFormError('Could not update this roster row.');
    } finally {
      setActionBookingId(null);
    }
  };

  const cancelBookingAndFindFiller = async (entry: TenantSessionRosterEntry) => {
    const wasBooked = (entry.bookingStatus || '').toUpperCase() === 'BOOKED';
    setActionBookingId(entry.bookingId);
    setFormError(null);
    try {
      await cancelRosterBooking.mutateAsync({
        occurrenceId,
        bookingId: entry.bookingId,
        payload: {
          cancelReason: wasBooked ? 'Cancelled from roster' : 'Removed from roster queue',
          openMakeupCredit: wasBooked,
          makeupNote: wasBooked ? 'Opened from roster cancellation' : null,
        },
      });
      await refetch();
      await refetchSlotFillers();
    } catch (error) {
      console.error('Failed to cancel roster booking', error);
      setFormError('Could not cancel this roster row.');
    } finally {
      setActionBookingId(null);
    }
  };

  const fillOpenSpot = async (suggestion: TenantSlotFillerSuggestion) => {
    const actionKey = `${suggestion.athleteId}-${suggestion.bookingId ?? 'new'}-${suggestion.makeupCreditId ?? 'none'}`;
    setSlotFillerActionKey(actionKey);
    setFormError(null);
    try {
      await applySlotFiller.mutateAsync({
        occurrenceId,
        payload: {
          athleteId: suggestion.athleteId,
          bookingId: suggestion.bookingId ?? null,
          makeupCreditId: suggestion.makeupCreditId ?? null,
          guardianProfileId: suggestion.guardianProfileId ?? null,
          note: suggestion.fromMakeupCredit ? 'Scheduled from slot-filler flow' : null,
        },
      });
      await refetch();
      await refetchSlotFillers();
    } catch (error) {
      console.error('Failed to fill open spot', error);
      setFormError('Could not fill this spot.');
    } finally {
      setSlotFillerActionKey(null);
    }
  };

  const recordBookingPaid = async (entry: TenantSessionRosterEntry) => {
    const amountText = window.prompt(
      'Amount paid outside TARK. Do not enter card or bank details.',
      entry.priceAmount != null ? String(entry.priceAmount) : ''
    );
    if (amountText == null) {
      return;
    }

    const amount = Number(amountText);
    if (!Number.isFinite(amount) || amount < 0) {
      setFormError('Enter a valid amount.');
      return;
    }

    const methodText = window.prompt('Method label only: CASH, CHECK, ZELLE, VENMO, CASH_APP, OTHER_OFF_PLATFORM', 'CASH');
    if (methodText == null) {
      return;
    }

    setPaymentActionBookingId(entry.bookingId);
    setFormError(null);
    try {
      await recordManualPayment.mutateAsync({
        bookingId: entry.bookingId,
        occurrenceId,
        athleteId: entry.athleteId,
        payload: {
          amount,
          currencyCode: entry.currencyCode || 'USD',
          manualPaymentMethod: methodText.trim() || 'CASH',
          paymentStatus: amount === 0 ? 'WAIVED' : 'PAID',
          note: 'Recorded from roster',
        },
      });
      await refetch();
    } catch (error) {
      console.error('Failed to record manual payment', error);
      setFormError('Could not record this payment. Do not enter card or bank details.');
    } finally {
      setPaymentActionBookingId(null);
    }
  };

  const coverBookingWithPackage = async (entry: TenantSessionRosterEntry) => {
    setPaymentActionBookingId(entry.bookingId);
    setFormError(null);
    try {
      await usePackageCredit.mutateAsync({
        bookingId: entry.bookingId,
        occurrenceId,
        athleteId: entry.athleteId,
        payload: {
          creditCost: 1,
          note: 'Covered from roster',
        },
      });
      await refetch();
    } catch (error) {
      console.error('Failed to use package credit', error);
      setFormError('No usable package credit was found for this student.');
    } finally {
      setPaymentActionBookingId(null);
    }
  };

  const updateMakeupStatus = async (entry: TenantSessionRosterEntry, creditStatus: string) => {
    setMakeupActionBookingId(entry.bookingId);
    setFormError(null);
    try {
      await updateMakeupCredit.mutateAsync({
        occurrenceId,
        bookingId: entry.bookingId,
        payload: {
          creditStatus,
          note: entry.makeupNote ?? null,
          scheduledOccurrenceId: entry.makeupScheduledOccurrenceId ?? null,
        },
      });
      await refetch();
    } catch (error) {
      console.error('Failed to update makeup follow-up', error);
      setFormError('Could not update makeup follow-up.');
    } finally {
      setMakeupActionBookingId(null);
    }
  };

  const renderMakeupControls = (entry: TenantSessionRosterEntry) => {
    if (!entry.makeupCandidate && !entry.makeupCreditId) {
      return null;
    }

    const status = (entry.makeupStatus || '').toUpperCase();
    const isPending = makeupActionBookingId === entry.bookingId && updateMakeupCredit.isPending;

    return (
      <div className="mt-3 flex flex-col gap-2 rounded-2xl border border-amber-200 bg-amber-50/50 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${makeupStatusClassName(entry.makeupStatus)}`}>
            {entry.makeupStatus ? formatScheduleCodeLabel(entry.makeupStatus) : 'Makeup candidate'}
          </span>
          <span className="text-xs font-medium text-amber-800">{makeupReasonLabel(entry)}</span>
        </div>

        {canManageSchedule ? (
          <div className="flex shrink-0 flex-wrap gap-1.5">
            {!entry.makeupCreditId ? (
              <Button size="sm" variant="outline" isLoading={isPending} onClick={() => updateMakeupStatus(entry, 'OPEN')}>
                Open makeup
              </Button>
            ) : status === 'OPEN' ? (
              <>
                <Button size="sm" variant="ghost" isLoading={isPending} onClick={() => updateMakeupStatus(entry, 'SCHEDULED')}>
                  Scheduled
                </Button>
                <Button size="sm" variant="ghost" isLoading={isPending} onClick={() => updateMakeupStatus(entry, 'USED')}>
                  Used
                </Button>
                <Button size="sm" variant="ghost" isLoading={isPending} onClick={() => updateMakeupStatus(entry, 'WAIVED')}>
                  Waive
                </Button>
              </>
            ) : status === 'SCHEDULED' ? (
              <>
                <Button size="sm" variant="ghost" isLoading={isPending} onClick={() => updateMakeupStatus(entry, 'OPEN')}>
                  Reopen
                </Button>
                <Button size="sm" variant="ghost" isLoading={isPending} onClick={() => updateMakeupStatus(entry, 'USED')}>
                  Used
                </Button>
                <Button size="sm" variant="ghost" isLoading={isPending} onClick={() => updateMakeupStatus(entry, 'WAIVED')}>
                  Waive
                </Button>
              </>
            ) : (
              <Button size="sm" variant="ghost" isLoading={isPending} onClick={() => updateMakeupStatus(entry, 'OPEN')}>
                Reopen
              </Button>
            )}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="mt-4 overflow-hidden rounded-[24px] border border-primary/20 bg-surface px-4 py-4 shadow-[var(--shadow-card)]">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <p className="text-base font-semibold text-text">Session roster</p>
          </div>
          {data ? (
            <p className="mt-1 text-sm text-text-muted">
              {formatDateRange(data.occurrence.startsAt, data.occurrence.endsAt)}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          {canManageSchedule ? (
            <Button size="sm" variant="outline" onClick={() => navigate(`/attendance?occurrenceId=${occurrenceId}`)}>
              Open Attendance
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="mt-4 flex items-center gap-3 rounded-2xl border border-border bg-surface-elevated/30 px-4 py-3">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          <p className="text-sm text-text-muted">Loading roster...</p>
        </div>
      ) : null}

      {isError ? (
        <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-sm font-medium text-amber-700">Roster unavailable right now.</p>
          <Button size="sm" variant="outline" className="mt-3" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      ) : null}

      {data ? (
        <div className="mt-4 space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
            <div className="rounded-2xl border border-border bg-surface-elevated/30 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle">Booked</p>
              <p className="mt-1 text-2xl font-semibold text-text">{data.rosterSummary.bookedCount}</p>
            </div>
            <div className="rounded-2xl border border-border bg-surface-elevated/30 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle">Requests</p>
              <p className="mt-1 text-2xl font-semibold text-text">{requestQueueCount}</p>
            </div>
            <div className="rounded-2xl border border-border bg-surface-elevated/30 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle">Waitlist</p>
              <p className="mt-1 text-2xl font-semibold text-text">{data.rosterSummary.waitlistedCount}</p>
            </div>
            <div className="rounded-2xl border border-border bg-surface-elevated/30 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle">Present</p>
              <p className="mt-1 text-2xl font-semibold text-text">{data.attendanceSummary.presentCount}</p>
            </div>
            <div className="rounded-2xl border border-border bg-surface-elevated/30 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle">Unmarked</p>
              <p className="mt-1 text-2xl font-semibold text-text">{data.attendanceSummary.unmarkedCount}</p>
            </div>
            <div className="rounded-2xl border border-border bg-surface-elevated/30 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle">Makeups</p>
              <p className="mt-1 text-2xl font-semibold text-text">{data.makeupSummary?.openCount ?? 0}</p>
            </div>
          </div>

          {canManageSchedule ? (
            <form onSubmit={handleAddAthlete} className="rounded-2xl border border-border bg-surface-elevated/30 p-3">
              <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-text">Add student</label>
                  <select
                    className="w-full rounded-lg border border-border bg-surface px-3.5 py-2.5 text-sm text-text focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                    value={selectedAthleteId}
                    onChange={(event) => setSelectedAthleteId(event.target.value)}
                    disabled={data.availableAthletes.length === 0}
                  >
                    <option value="">
                      {data.availableAthletes.length > 0 ? 'Choose from active students' : 'No available students'}
                    </option>
                    {data.availableAthletes.map((candidate) => (
                      <option key={candidate.athleteId} value={candidate.athleteId}>
                        {candidate.displayName}
                        {candidateMeta(candidate) ? ` - ${candidateMeta(candidate)}` : ''}
                      </option>
                    ))}
                  </select>
                  <div className="flex flex-wrap gap-2">
                    {rosterAddStatuses.map((status) => {
                      const selected = newBookingStatus === status.value;
                      return (
                        <button
                          key={status.value}
                          type="button"
                          onClick={() => setNewBookingStatus(status.value)}
                          className={`rounded-full border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors ${
                            selected
                              ? rosterStatusClassName(status.value)
                              : 'border-border bg-surface text-text-subtle hover:border-primary/30 hover:text-primary'
                          }`}
                        >
                          {status.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <Button type="submit" isLoading={addRosterAthlete.isPending} disabled={data.availableAthletes.length === 0}>
                  {newBookingStatus === 'BOOKED' ? 'Add' : 'Queue'}
                </Button>
              </div>
              {formError ? <p className="mt-2 text-sm text-red-500">{formError}</p> : null}
            </form>
          ) : null}

          {canManageSchedule && openSpotCount > 0 ? (
            <div className="rounded-2xl border border-primary/15 bg-primary/5 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-text">Fill open spot</p>
                  <p className="mt-0.5 text-xs text-text-muted">
                    {openSpotCount} open {openSpotCount === 1 ? 'spot' : 'spots'}
                    {isFetchingSlotFillers ? ' • checking options' : ''}
                  </p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => refetchSlotFillers()} isLoading={isFetchingSlotFillers}>
                  Refresh
                </Button>
              </div>

              {slotFillerSuggestions.length > 0 ? (
                <div className="mt-3 grid gap-2 lg:grid-cols-2">
                  {slotFillerSuggestions.slice(0, 4).map((suggestion) => {
                    const actionKey = `${suggestion.athleteId}-${suggestion.bookingId ?? 'new'}-${suggestion.makeupCreditId ?? 'none'}`;
                    return (
                      <div key={actionKey} className="rounded-2xl border border-border bg-white/80 p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-semibold text-text">{suggestion.athleteDisplayName}</p>
                              <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ${slotFillerSourceClassName(suggestion.sourceType)}`}>
                                {slotFillerSourceLabel(suggestion.sourceType)}
                              </span>
                              {suggestion.matchedAvailability ? (
                                <span className="rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-primary">
                                  Available
                                </span>
                              ) : null}
                            </div>
                            <p className="mt-1 line-clamp-2 text-xs leading-5 text-text-muted">
                              {slotFillerMeta(suggestion)}
                            </p>
                            {(suggestion.warnings ?? []).length > 0 ? (
                              <p className="mt-1 text-[11px] font-medium text-amber-700">
                                {(suggestion.warnings ?? []).join(' • ')}
                              </p>
                            ) : null}
                          </div>
                          <Button
                            size="sm"
                            isLoading={slotFillerActionKey === actionKey && applySlotFiller.isPending}
                            onClick={() => fillOpenSpot(suggestion)}
                          >
                            Fill
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="mt-3 rounded-2xl border border-dashed border-primary/20 bg-white/60 px-3 py-2">
                  <p className="text-sm text-text-muted">
                    {isFetchingSlotFillers ? 'Finding safe options...' : 'No safe filler options yet. Add student availability or use Add student.'}
                  </p>
                </div>
              )}
            </div>
          ) : null}

          {activeRoster.length > 0 ? (
            <div className="space-y-2">
              {activeRoster.map((entry) => (
                <div key={entry.bookingId} className="rounded-2xl border border-border bg-white/70 px-4 py-3">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-semibold text-text">{entry.athleteDisplayName}</p>
                        <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${rosterStatusClassName(entry.bookingStatus)}`}>
                          {formatScheduleCodeLabel(entry.bookingStatus)}
                        </span>
                        {(entry.bookingStatus || '').toUpperCase() === 'BOOKED' ? (
                          <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${attendanceStatusClassName(entry.attendanceStatus)}`}>
                            {entry.attendanceStatus ? formatScheduleCodeLabel(entry.attendanceStatus) : 'Unmarked'}
                          </span>
                        ) : null}
                        <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${paymentStatusClassName(entry.paymentStatus)}`}>
                          {formatScheduleCodeLabel(entry.paymentStatus || 'PENDING')}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-text-muted">{rosterEntryMeta(entry) || 'Roster row ready'}</p>
                    </div>
                    {canManageSchedule ? (
                      <div className="flex shrink-0 flex-col gap-2 lg:items-end">
                        <div className="flex flex-wrap gap-2 lg:justify-end">
                          {(entry.bookingStatus || '').toUpperCase() === 'BOOKED' ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => navigate(`/attendance?occurrenceId=${occurrenceId}`)}
                            >
                              Attendance
                            </Button>
                          ) : null}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/directory?athleteId=${entry.athleteId}`)}
                          >
                            Profile
                          </Button>
                          {(entry.bookingStatus || '').toUpperCase() === 'BOOKED' ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              isLoading={actionBookingId === entry.bookingId && updateRosterBooking.isPending}
                              onClick={() => updateBookingStatus(entry, 'WAITLISTED')}
                            >
                              Waitlist
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              isLoading={actionBookingId === entry.bookingId && updateRosterBooking.isPending}
                              onClick={() => updateBookingStatus(entry, 'BOOKED')}
                            >
                              Book
                            </Button>
                          )}
                          {(entry.bookingStatus || '').toUpperCase() === 'BOOKED' &&
                          !['PAID', 'PACKAGE', 'WAIVED'].includes((entry.paymentStatus || '').toUpperCase()) ? (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                isLoading={paymentActionBookingId === entry.bookingId && usePackageCredit.isPending}
                                onClick={() => coverBookingWithPackage(entry)}
                              >
                                Use package
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                isLoading={paymentActionBookingId === entry.bookingId && recordManualPayment.isPending}
                                onClick={() => recordBookingPaid(entry)}
                              >
                                Paid
                              </Button>
                            </>
                          ) : null}
                          {['REQUESTED', 'TRIAL_REQUESTED'].includes((entry.bookingStatus || '').toUpperCase()) ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              isLoading={actionBookingId === entry.bookingId && updateRosterBooking.isPending}
                              onClick={() => updateBookingStatus(entry, 'WAITLISTED')}
                            >
                              Waitlist
                            </Button>
                          ) : null}
                          <Button
                            size="sm"
                            variant="outline"
                            isLoading={actionBookingId === entry.bookingId && (updateRosterBooking.isPending || cancelRosterBooking.isPending)}
                            onClick={() => cancelBookingAndFindFiller(entry)}
                          >
                            {['REQUESTED', 'TRIAL_REQUESTED'].includes((entry.bookingStatus || '').toUpperCase()) ? 'Decline' : 'Remove'}
                          </Button>
                        </div>
                      </div>
                    ) : null}
                  </div>

                  {entry.progressSummary ? (
                    <div className="mt-3 rounded-2xl border border-primary/10 bg-primary/5 px-3 py-2">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">Session note</p>
                      <p className="mt-1 text-sm leading-6 text-text">{entry.progressSummary}</p>
                    </div>
                  ) : null}

                  {renderMakeupControls(entry)}
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-border bg-surface-elevated/20 p-4">
              <p className="text-sm font-semibold text-text">No students booked yet</p>
            </div>
          )}

          {cancelledRoster.length > 0 ? (
            <details className="rounded-2xl border border-border bg-surface-elevated/20 px-4 py-3">
              <summary className="cursor-pointer text-sm font-semibold text-text">
                Removed students ({cancelledRoster.length})
              </summary>
              <div className="mt-3 space-y-2">
                {cancelledRoster.map((entry) => (
                  <div key={entry.bookingId} className="rounded-xl border border-border bg-surface px-3 py-2">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm font-semibold text-text">{entry.athleteDisplayName}</p>
                        <p className="text-xs text-text-muted">
                          {entry.cancelledAt ? `Removed ${formatShortDateTime(entry.cancelledAt)}` : 'Removed'}
                        </p>
                      </div>
                      {canManageSchedule ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          isLoading={actionBookingId === entry.bookingId && updateRosterBooking.isPending}
                          onClick={() => updateBookingStatus(entry, 'BOOKED')}
                        >
                          Rebook
                        </Button>
                      ) : null}
                    </div>
                    {renderMakeupControls(entry)}
                  </div>
                ))}
              </div>
            </details>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default function SchedulePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { activeTenant } = useTenantBootstrap(Boolean(user));
  const workSurface = activeTenant?.tenant?.workSurface?.toLowerCase() || 'schedule';
  const workTabLabel = activeTenant?.tenant?.workTabLabel || 'Schedule';
  const tenantArchetype = activeTenant?.tenant?.tenantArchetype || null;
  const directoryTabLabel =
    tenantArchetype === 'COACHING'
      ? 'Students'
      : activeTenant?.tenant?.directoryTabLabel || 'People';
  const { data, isLoading, isError, refetch } = useScheduleDashboard(Boolean(user) && workSurface === 'schedule');
  const tenantDirectory = useTenantDirectory(Boolean(user) && workSurface === 'schedule' && tenantArchetype === 'COACHING');
  const {
    data: workData,
    isLoading: isWorkLoading,
    isError: isWorkError,
    refetch: refetchWork,
  } = useTenantWorkDashboard(Boolean(user) && Boolean(activeTenant?.tenant?.id) && workSurface === 'orders');
  const updateWorkRequest = useUpdateTenantWorkRequest();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingOccurrenceId, setEditingOccurrenceId] = useState<number | null>(null);
  const [selectedRosterOccurrenceId, setSelectedRosterOccurrenceId] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [cancellingOccurrenceId, setCancellingOccurrenceId] = useState<number | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [formState, setFormState] = useState(defaultOccurrenceForm);
  const [editFormError, setEditFormError] = useState<string | null>(null);
  const [editFormState, setEditFormState] = useState(defaultOccurrenceForm);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const [scheduleView, setScheduleView] = useState<ScheduleViewMode>('list');
  const [requestNoteDrafts, setRequestNoteDrafts] = useState<Record<number, string>>({});
  const [requestActionId, setRequestActionId] = useState<number | null>(null);
  const ordersWorkspaceCopy = useMemo(
    () => resolveOrdersWorkspaceCopy(tenantArchetype, directoryTabLabel, workTabLabel),
    [directoryTabLabel, tenantArchetype, workTabLabel]
  );

  const canManageSchedule = useMemo(() => {
    const role = data?.roleCode?.toUpperCase();
    return role === 'OWNER' || role === 'ADMIN' || role === 'COACH' || role === 'STAFF';
  }, [data?.roleCode]);

  const safeScheduleSubtitle = useMemo(() => {
    if (!data) {
      return '';
    }

    if (data.mode === 'TENANT' && data.coachDashboard) {
      return data.coachDashboard.subtitle
        || `Manage ${workTabLabel.toLowerCase()}, ${directoryTabLabel.toLowerCase()}, access, and program health from this workspace.`;
    }

    if (data.mode === 'TENANT') {
      return `Manage ${workTabLabel.toLowerCase()}, ${directoryTabLabel.toLowerCase()}, and tenant access from this workspace.`;
    }

    return data.subtitle;
  }, [data, directoryTabLabel, workTabLabel]);

  const visibleActions = useMemo(() => {
    if (!data) {
      return [];
    }

    if (data.mode !== 'TENANT') {
      return data.actions;
    }

    if (data.coachDashboard) {
      return [];
    }

    return [
      {
        key: 'schedule',
        label: workTabLabel,
        description: 'Official sessions, timing, capacity, and coach assignment.',
      },
      {
        key: 'directory',
        label: directoryTabLabel,
        description: 'Students, parents, guardians, staff, and roster context.',
      },
      canManageSchedule
        ? {
            key: 'access',
            label: 'Access',
            description: 'Guardian join codes, staff invitations, and membership actions.',
          }
        : {
            key: 'family',
            label: 'Family',
            description: 'Household athletes connected to this organization.',
          },
    ];
  }, [canManageSchedule, data, directoryTabLabel, workTabLabel]);

  const visibleWorkActions = useMemo(
    () => (workData?.actions || []).filter((action) => !['buzz', 'chats'].includes(action.key)),
    [workData?.actions]
  );

  const scheduleOverview = useMemo(() => {
    if (!data) {
      return [];
    }

    const todayLabel = new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    const todayCount = data.upcomingOccurrences.filter((occurrence) => {
      const startsAt = new Date(occurrence.startsAt);
      const today = new Date();
      return startsAt.toDateString() === today.toDateString();
    }).length;

    return [
      {
        label: 'Today',
        value: String(todayCount),
        description: `Sessions on ${todayLabel}`,
      },
      {
        label: 'Upcoming',
        value: String(data.upcomingOccurrences.length),
        description: 'Scheduled sessions currently visible',
      },
      {
        label: 'Programs',
        value: String(data.programs.length),
        description: 'Programs currently feeding this schedule',
      },
    ];
  }, [data]);
  const selectedRosterOccurrenceIsVisible = Boolean(
    selectedRosterOccurrenceId &&
      data?.upcomingOccurrences.some(
        (occurrence) => occurrence.occurrenceId === selectedRosterOccurrenceId
      )
  );
  const linkedCoachStudents = tenantDirectory.directory?.access?.linkedAthleteCount ?? 0;
  const isCoachStudioSchedule = data?.mode === 'TENANT' && Boolean(data.coachDashboard);
  const coachTimePollsEnabled = isCoachStudioSchedule && activeTenant?.config?.coachTimePollsEnabled === true;
  const hasCoachSessions = (data?.upcomingOccurrences.length ?? 0) > 0;
  const isNewCoachSchedule = Boolean(isCoachStudioSchedule && !hasCoachSessions && linkedCoachStudents === 0);

  const openCreateSession = () => {
    setIsCreateOpen(true);
    setFormError(null);
    if (!formState.programId && data?.programs[0]) {
      setFormState((current) => ({ ...current, programId: String(data.programs[0].programId) }));
    }
  };

  const useSmartScheduleSuggestion = (suggestion: TenantSmartScheduleSuggestion, smartForm: SmartScheduleFormState) => {
    setIsCreateOpen(true);
    setFormError(null);
    setActionNotice('Smart schedule option loaded into Create Session. Review, then create when ready.');
    setFormState((current) => ({
      ...current,
      programId: smartForm.programId || current.programId,
      startsAt: toDateTimeLocalValue(new Date(suggestion.startsAt)),
      endsAt: toDateTimeLocalValue(new Date(suggestion.endsAt)),
      locationLabel: suggestion.locationLabel || smartForm.locationLabel || current.locationLabel,
      capacity: smartForm.capacity || (suggestion.capacity ? String(suggestion.capacity) : current.capacity),
    }));
    window.setTimeout(() => {
      document.getElementById('upcoming-sessions')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  const resetForm = () => {
    setFormError(null);
    setFormState(defaultOccurrenceForm());
  };

  const resetEditForm = () => {
    setEditFormError(null);
    setEditFormState(defaultOccurrenceForm());
    setEditingOccurrenceId(null);
  };

  const requestActions = (status?: string | null) => {
    const normalized = status?.trim().toUpperCase().replace(/[\s-]+/g, '_');
    switch (normalized) {
      case 'NEW':
        return [{ label: 'Start', status: 'IN_PROGRESS', assignToMe: true }];
      case 'IN_PROGRESS':
        return [
          { label: 'Responded', status: 'RESPONDED', assignToMe: true },
          { label: 'Close', status: 'CLOSED', assignToMe: false },
        ];
      case 'RESPONDED':
        return [
          { label: 'Complete', status: 'COMPLETED', assignToMe: true },
          { label: 'Close', status: 'CLOSED', assignToMe: false },
        ];
      default:
        return [];
    }
  };

  const handleRequestUpdate = async (
    requestId: number,
    requestStatus?: string,
    assignToMe: boolean = true
  ) => {
    try {
      setRequestActionId(requestId);
      setActionNotice(null);
      await updateWorkRequest.mutateAsync({
        requestId,
        payload: {
          requestStatus,
          providerNote: requestNoteDrafts[requestId]?.trim() || undefined,
          assignToMe,
        },
      });
      await refetchWork();
      setActionNotice('Request workflow updated.');
    } catch (error) {
      console.error('Failed to update work request', error);
      window.alert('Could not update the request right now.');
    } finally {
      setRequestActionId(null);
    }
  };

  const startEditingOccurrence = (occurrence: NonNullable<typeof data>['upcomingOccurrences'][number]) => {
    setEditFormError(null);
    setEditingOccurrenceId(occurrence.occurrenceId);
    setEditFormState({
      programId: String(occurrence.programId ?? ''),
      title: occurrence.title ?? '',
      startsAt: toDateTimeLocalValue(new Date(occurrence.startsAt)),
      endsAt: toDateTimeLocalValue(new Date(occurrence.endsAt)),
      locationLabel: occurrence.locationLabel ?? '',
      capacity: occurrence.capacity != null ? String(occurrence.capacity) : '',
    });
  };

  const handleCreateOccurrence = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!data) return;

    if (!formState.programId) {
      setFormError('Choose a program before creating a session.');
      return;
    }

    setIsCreating(true);
    setFormError(null);
    setActionNotice(null);
    try {
      await createScheduleOccurrence({
        programId: Number(formState.programId),
        title: formState.title.trim() || undefined,
        startsAt: new Date(formState.startsAt).toISOString(),
        endsAt: new Date(formState.endsAt).toISOString(),
        locationLabel: formState.locationLabel.trim() || undefined,
        capacity: formState.capacity ? Number(formState.capacity) : undefined,
      });
      await refetch();
      setIsCreateOpen(false);
      resetForm();
      setActionNotice('The session was scheduled, and the linked group chat was notified when available.');
    } catch (error) {
      console.error('Failed to create schedule occurrence', error);
      setFormError('Could not create the session. Check the timing and try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancelOccurrence = async (occurrenceId: number) => {
    if (!window.confirm('Cancel this session, close booking, and notify the linked group chat when available?')) {
      return;
    }

    setCancellingOccurrenceId(occurrenceId);
    setActionNotice(null);
    try {
      await cancelScheduleOccurrence(occurrenceId, {
        cancelReason: 'Cancelled by coach from schedule',
        openMakeupCredits: true,
        makeupNote: 'Opened from session cancellation',
      });
      await refetch();
      setActionNotice('The session was cancelled. Makeup follow-up opened for booked students when needed.');
    } catch (error) {
      console.error('Failed to cancel schedule occurrence', error);
      window.alert('Could not cancel the session right now.');
    } finally {
      setCancellingOccurrenceId(null);
    }
  };

  const handleUpdateOccurrence = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editingOccurrenceId) return;

    setIsUpdating(true);
    setEditFormError(null);
    setActionNotice(null);
    try {
      await updateScheduleOccurrence(editingOccurrenceId, {
        title: editFormState.title.trim() || undefined,
        startsAt: new Date(editFormState.startsAt).toISOString(),
        endsAt: new Date(editFormState.endsAt).toISOString(),
        locationLabel: editFormState.locationLabel.trim() || undefined,
        capacity: editFormState.capacity ? Number(editFormState.capacity) : undefined,
      });
      await refetch();
      resetEditForm();
      setActionNotice('The session was updated, and the linked group chat was notified when available.');
    } catch (error) {
      console.error('Failed to update schedule occurrence', error);
      setEditFormError('Could not update the session. Check the timing and try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    if (workSurface === 'orders') {
      return (
        <div className="min-h-[50vh] flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      );
    }

    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (workSurface === 'orders' && isWorkLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (activeTenant?.tenant?.id && workSurface === 'orders' && workData && !isWorkError) {
    return (
      <div className="w-full space-y-8 pb-24 md:pb-8">
        <div className="pt-2">
          <p className="text-sm text-text-muted mb-1">{workData.workTabLabel}</p>
          <h1 className="text-2xl font-bold text-text tracking-tight">{workData.greetingName}</h1>
          <p className="mt-3 text-3xl font-bold text-text tracking-tight">{workData.headline}</p>
          <p className="mt-2 max-w-3xl text-sm text-text-muted">{workData.subtitle}</p>
        </div>

        <Card variant="glass" className="overflow-hidden">
          <CardContent className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-text-muted">{workData.contextLabel}</p>
              <p className="mt-2 text-lg font-semibold text-text">
                {workData.activeTenant?.displayName || activeTenant.tenant.displayName}
              </p>
              <p className="mt-1 text-sm text-text-muted">
                {workData.roleCode
                  ? `${formatRoleLabel(workData.roleCode)} • ${workData.directoryTabLabel}`
                  : `${workData.membershipsCount} organization memberships available`}
              </p>
            </div>
            <div className="rounded-xl border border-border bg-surface/60 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-text-muted">Workspace Focus</p>
              <p className="mt-1 font-semibold text-text">{workData.workTabLabel}</p>
              <p className="mt-1 text-xs leading-5 text-text-muted">{ordersWorkspaceCopy.workflowTitle}</p>
            </div>
          </CardContent>
        </Card>

        <Card variant="default">
          <CardContent className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-base font-semibold text-text">{ordersWorkspaceCopy.workflowTitle}</p>
              <p className="mt-1 text-sm text-text-muted">{ordersWorkspaceCopy.workflowDescription}</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" leftIcon={<Users className="h-4 w-4" />} onClick={() => navigate('/directory')}>
                Open {workData.directoryTabLabel}
              </Button>
              <Button variant="outline" leftIcon={<ShieldCheck className="h-4 w-4" />} onClick={() => navigate('/tenant-access')}>
                Open Access
              </Button>
              <Button onClick={() => navigate('/pros')}>
                Open PROs
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-3 md:grid-cols-3">
          {visibleWorkActions.map((action) => (
            <button
              key={action.key}
              type="button"
              onClick={() => navigate(actionRoutes[action.key] || '/profile')}
              className="group rounded-2xl border border-border bg-surface p-4 text-left transition-all duration-200 hover:border-primary/40 hover:bg-surface-elevated"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="rounded-xl bg-primary/10 p-3 text-primary">
                  {actionIcons[action.key] || <ArrowRight className="h-5 w-5" />}
                </div>
                <ArrowRight className="mt-1 h-4 w-4 text-text-subtle transition-colors group-hover:text-primary" />
              </div>
              <p className="mt-4 text-base font-semibold text-text">{action.label}</p>
              <p className="mt-1 text-sm text-text-muted">{action.description}</p>
            </button>
          ))}
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {workData.stats.map((stat) => (
            <Card key={stat.key} variant="default" className="h-full">
              <CardContent className="space-y-2">
                <p className="text-xs uppercase tracking-wide text-text-muted">{stat.label}</p>
                <p className="text-2xl font-bold text-text">{stat.value}</p>
                <p className="text-sm text-text-muted">{stat.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
          <Card variant="default">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-primary" />
                {ordersWorkspaceCopy.queueTitle}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-text-muted">{ordersWorkspaceCopy.queueDescription}</p>
              {workData.recentRequests.length > 0 ? (
                workData.recentRequests.map((request) => (
                  <div key={request.requestId} className="rounded-2xl border border-border bg-surface-elevated/50 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-base font-semibold text-text">{request.requesterDisplayName}</p>
                        <p className="mt-1 text-sm text-text-muted">{request.summary}</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <div className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary">
                          {request.contactMethod}
                        </div>
                        <div className="rounded-full border border-border px-3 py-1 text-xs font-semibold uppercase tracking-wide text-text-muted">
                          {request.requestStatus}
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 grid gap-2 text-sm text-text-muted">
                      {request.requesterProfileName ? (
                        <div className="flex items-center gap-2">
                          <UserCircle2 className="h-4 w-4 text-primary" />
                          <span>{request.requesterProfileName}</span>
                        </div>
                      ) : null}
                      {request.requesterPhone ? (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-primary" />
                          <span>{request.requesterPhone}</span>
                        </div>
                      ) : null}
                      {request.requesterEmail ? (
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-primary" />
                          <span>{request.requesterEmail}</span>
                        </div>
                      ) : null}
                      <div className="flex items-center gap-2">
                        <Clock3 className="h-4 w-4 text-primary" />
                        <span>
                          {new Date(request.updatedAt || request.createdAt).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                      {request.assignedDisplayName ? (
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-primary" />
                          <span>Assigned to {request.assignedDisplayName}</span>
                        </div>
                      ) : null}
                    </div>

                    <div className="mt-4 grid gap-3">
                      <Input
                        label="Internal Note"
                        value={requestNoteDrafts[request.requestId] ?? request.providerNote ?? ''}
                        onChange={(event) =>
                          setRequestNoteDrafts((current) => ({
                            ...current,
                            [request.requestId]: event.target.value,
                          }))
                        }
                        placeholder={ordersWorkspaceCopy.notePlaceholder}
                      />
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          onClick={() => handleRequestUpdate(request.requestId, request.requestStatus, false)}
                          isLoading={requestActionId === request.requestId && updateWorkRequest.isPending}
                        >
                          Save Note
                        </Button>
                        {requestActions(request.requestStatus).map((action) => (
                          <Button
                            key={`${request.requestId}-${action.status}`}
                            variant={action.status === 'CLOSED' ? 'outline' : 'primary'}
                            onClick={() => handleRequestUpdate(request.requestId, action.status, action.assignToMe)}
                            isLoading={requestActionId === request.requestId && updateWorkRequest.isPending}
                          >
                            {action.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded-2xl border border-dashed border-border bg-surface-elevated/40 p-5">
                  <p className="text-base font-semibold text-text">{workData.emptyState?.title || 'No inbound requests yet'}</p>
                  <p className="mt-2 text-sm text-text-muted">
                    {workData.emptyState?.description || ordersWorkspaceCopy.emptyDescription}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card variant="default">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary" />
                  {ordersWorkspaceCopy.listingTitle}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {workData.linkedProvider ? (
                  <>
                    <div>
                      <p className="font-semibold text-text">{workData.linkedProvider.displayName}</p>
                      <p className="mt-1 text-sm text-text-muted">
                        {[workData.linkedProvider.city, workData.linkedProvider.state].filter(Boolean).join(', ') || 'Location pending'}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {workData.linkedProvider.providerTypes.map((type) => (
                        <span key={type} className="rounded-full border border-border bg-surface-elevated px-3 py-1 text-xs font-semibold text-text-muted">
                          {type}
                        </span>
                      ))}
                      {workData.linkedProvider.publicationStatus ? (
                        <span className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                          {workData.linkedProvider.publicationStatus}
                        </span>
                      ) : null}
                    </div>
                    <p className="text-sm text-text-muted">
                      Preferred contact: {workData.linkedProvider.preferredContactMethod || 'Not set'}
                    </p>
                    <p className="text-sm text-text-muted">{ordersWorkspaceCopy.listingDescription}</p>
                  </>
                ) : (
                  <p className="text-sm text-text-muted">
                    {workData.emptyState?.description || ordersWorkspaceCopy.emptyDescription}
                  </p>
                )}
              </CardContent>
            </Card>

            <Card variant="default">
              <CardHeader>
                <CardTitle>{ordersWorkspaceCopy.guidanceTitle}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-text-muted">
                {ordersWorkspaceCopy.guidancePoints.map((point) => (
                  <p key={point}>{point}</p>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (workSurface === 'orders') {
    return (
      <Card variant="default" className="max-w-xl">
        <CardHeader>
          <CardTitle>{workTabLabel} Unavailable</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-text-muted">
            The work dashboard could not be loaded right now. Try again and we’ll restore the active tenant context.
          </p>
          <Button onClick={() => refetchWork()} leftIcon={<ArrowRight className="h-4 w-4" />}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (isError || !data) {
    return (
      <Card variant="default" className="max-w-xl">
        <CardHeader>
          <CardTitle>Schedule Unavailable</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-text-muted">
            The schedule shell could not be loaded right now. Try again and we’ll restore your current context.
          </p>
          <Button onClick={() => refetch()} leftIcon={<ArrowRight className="h-4 w-4" />}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (activeTenant?.tenant?.id && workSurface !== 'schedule') {
    return (
      <div className="space-y-6 pb-24 md:pb-8 max-w-4xl">
        <CoachPageHeader
          eyebrow={workTabLabel}
          title={activeTenant.tenant.displayName}
          subtitle={`This workspace uses ${workTabLabel.toLowerCase()} for its operating surface.`}
          meta={
            <span className="rounded-full border border-border bg-surface px-3 py-1 text-xs font-semibold text-text-muted">
              {activeTenant.roleCode} • {directoryTabLabel}
            </span>
          }
        />

        <Card variant="glass">
          <CardContent className="space-y-3">
            <p className="text-base font-semibold text-text">
              {activeTenant.roleCode} • {directoryTabLabel}
            </p>
            <p className="text-sm text-text-muted">
              While this work flow is being built out, use the tenant directory and Me surfaces for household, roster,
              and membership management.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => navigate('/directory')}>Open {directoryTabLabel}</Button>
              <Button variant="outline" onClick={() => navigate('/tenant-access')}>Open Access</Button>
              <Button variant="outline" onClick={() => navigate('/profile')}>Open Me</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full space-y-8 pb-24 md:pb-8">
      <CoachPageHeader
        eyebrow={isCoachStudioSchedule ? 'Coach Studio' : workTabLabel}
        title={isCoachStudioSchedule ? 'Schedule' : data.activeTenant?.displayName || data.greetingName}
        subtitle={isCoachStudioSchedule ? 'Create sessions, manage the week, and open rosters from one schedule.' : safeScheduleSubtitle}
        meta={
          <span className="rounded-full border border-border bg-surface px-3 py-1 text-xs font-semibold text-text-muted">
            {data.activeTenant?.displayName || 'Shared TARK community'}
          </span>
        }
        action={
          data.mode === 'TENANT' && canManageSchedule ? (
            <>
              {coachTimePollsEnabled ? (
                <Button variant="outline" leftIcon={<ListChecks className="h-4 w-4" />} onClick={() => navigate('/schedule/time-polls')}>
                  Time polls
                </Button>
              ) : null}
              {data.programs.length > 0 ? (
                <Button leftIcon={<Calendar className="h-4 w-4" />} onClick={openCreateSession}>
                  Create Session
                </Button>
              ) : (
                <Button leftIcon={<Calendar className="h-4 w-4" />} onClick={() => navigate('/classes')}>
                  Create Class
                </Button>
              )}
            </>
          ) : null
        }
      />

      {actionNotice ? (
        <div className="rounded-2xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-text">
          {actionNotice}
        </div>
      ) : null}

      {data.mode === 'TENANT' && data.coachDashboard && !isNewCoachSchedule ? (
        <CoachAvailabilityPanel
          enabled={Boolean(user) && canManageSchedule}
          canManageSchedule={canManageSchedule}
          timezone={data.coachDashboard.availability?.timezone || data.coachDashboard.timezone}
          onAvailabilityChanged={() => refetch()}
        />
      ) : null}

      {data.mode === 'TENANT' && data.coachDashboard && canManageSchedule ? (
        <CoachBookableOfferingsPanel enabled={Boolean(user) && canManageSchedule} />
      ) : null}

      {data.mode === 'TENANT' && data.coachDashboard && !isNewCoachSchedule ? (
        <StudentSmartSchedulePanel
          enabled={Boolean(user) && canManageSchedule}
          canManageSchedule={canManageSchedule}
          timezone={data.coachDashboard.availability?.timezone || data.coachDashboard.timezone}
          programs={data.programs}
          onAvailabilityChanged={() => refetch()}
          onUseSuggestion={useSmartScheduleSuggestion}
        />
      ) : null}

      {data.mode === 'TENANT' && canManageSchedule && selectedRosterOccurrenceId && (!selectedRosterOccurrenceIsVisible || scheduleView === 'week') ? (
        <SessionRosterPanel
          occurrenceId={selectedRosterOccurrenceId}
          canManageSchedule={canManageSchedule}
          onClose={() => setSelectedRosterOccurrenceId(null)}
        />
      ) : null}

      {data.mode === 'TENANT' && !data.coachDashboard ? (
        <div className="grid gap-3 md:grid-cols-3">
          {scheduleOverview.map((item) => (
            <Card key={item.label} variant="default" className="h-full">
              <CardContent className="space-y-2">
                <p className="text-xs uppercase tracking-[0.18em] text-text-muted">{item.label}</p>
                <p className="text-2xl font-bold text-text">{item.value}</p>
                <p className="text-sm text-text-muted">{item.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : null}

      {data.mode === 'TENANT' && !data.coachDashboard ? (
        <Card variant="default">
          <CardContent className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-base font-semibold text-text">Coaching workflow</p>
              <p className="mt-1 text-sm text-text-muted">
                Run the day from {workTabLabel}, then move into {directoryTabLabel} or Access when you need roster, guardian, or follow-up work.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" leftIcon={<Users className="h-4 w-4" />} onClick={() => navigate('/directory')}>
                Open {directoryTabLabel}
              </Button>
              <Button variant="outline" leftIcon={<ShieldCheck className="h-4 w-4" />} onClick={() => navigate('/tenant-access')}>
                Open Access
              </Button>
              {canManageSchedule ? (
                <Button
                  onClick={openCreateSession}
                >
                  Create Session
                </Button>
              ) : null}
            </div>
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-3 md:grid-cols-3">
        {visibleActions.map((action) => (
          <button
            key={action.key}
            type="button"
            onClick={() => navigate(actionRoutes[action.key] || '/profile')}
            className="group rounded-2xl border border-border bg-surface p-4 text-left transition-all duration-200 hover:border-primary/40 hover:bg-surface-elevated"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="rounded-xl bg-primary/10 p-3 text-primary">
                {actionIcons[action.key] || <ArrowRight className="h-5 w-5" />}
              </div>
              <ArrowRight className="mt-1 h-4 w-4 text-text-subtle transition-colors group-hover:text-primary" />
            </div>
            <p className="mt-4 text-base font-semibold text-text">{action.label}</p>
            <p className="mt-1 text-sm text-text-muted">{action.description}</p>
          </button>
        ))}
      </div>

      {!data.coachDashboard ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {data.stats.map((stat) => (
            <Card key={stat.key} variant="default" className="h-full">
              <CardContent className="space-y-2">
                <p className="text-xs uppercase tracking-wide text-text-muted">{stat.label}</p>
                <p className="text-2xl font-bold text-text">{stat.value}</p>
                <p className="text-sm text-text-muted">{stat.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : null}

      <div className={data.coachDashboard ? 'grid gap-6' : 'grid gap-6 xl:grid-cols-[1.25fr_0.75fr]'}>
        <Card id="upcoming-sessions" variant="default">
          <CardHeader>
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                Upcoming Sessions
              </CardTitle>
              <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                {canManageSchedule && data.mode === 'TENANT' ? (
                  <a
                    href="/api/v1/web/schedule/calendar.ics"
                    className="inline-flex h-9 items-center justify-center rounded-lg border border-border px-3 text-sm font-medium text-text transition-all duration-200 hover:border-primary/40 hover:text-primary"
                  >
                    Calendar Feed
                  </a>
                ) : null}
                {data.upcomingOccurrences.length > 0 ? (
                  <div className="inline-flex rounded-lg border border-border bg-surface p-1">
                    {(['list', 'week'] as const).map((view) => (
                      <button
                        key={view}
                        type="button"
                        className={cn(
                          'rounded-md px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.12em] transition-colors',
                          scheduleView === view
                            ? 'bg-primary [color:var(--color-on-primary)]'
                            : 'text-text-muted hover:text-text'
                        )}
                        onClick={() => setScheduleView(view)}
                      >
                        {view}
                      </button>
                    ))}
                  </div>
                ) : null}
                {canManageSchedule && data.mode === 'TENANT' && !isNewCoachSchedule ? (
                  <Button
                    size="sm"
                    variant={isCreateOpen ? 'secondary' : 'outline'}
                    onClick={() => {
                      const next = !isCreateOpen;
                      setIsCreateOpen(next);
                      setFormError(null);
                      if (next && !formState.programId && data.programs[0]) {
                        setFormState((current) => ({ ...current, programId: String(data.programs[0].programId) }));
                      }
                    }}
                  >
                    {isCreateOpen ? 'Close' : 'Create Session'}
                  </Button>
                ) : null}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {canManageSchedule && isCreateOpen ? (
              <form onSubmit={handleCreateOccurrence} className="rounded-2xl border border-border bg-surface-elevated/50 p-4 space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-text">Program</label>
                    <select
                      className="w-full rounded-lg bg-surface border border-border px-3.5 py-2.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                      value={formState.programId}
                      onChange={(event) => setFormState((current) => ({ ...current, programId: event.target.value }))}
                    >
                      <option value="">Select a program</option>
                      {data.programs.map((program) => (
                        <option key={program.programId} value={program.programId}>
                          {program.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Input
                    label="Session Title"
                    placeholder="Optional override title"
                    value={formState.title}
                    onChange={(event) => setFormState((current) => ({ ...current, title: event.target.value }))}
                  />
                  <Input
                    label="Starts At"
                    type="datetime-local"
                    value={formState.startsAt}
                    onChange={(event) => setFormState((current) => ({ ...current, startsAt: event.target.value }))}
                  />
                  <Input
                    label="Ends At"
                    type="datetime-local"
                    value={formState.endsAt}
                    onChange={(event) => setFormState((current) => ({ ...current, endsAt: event.target.value }))}
                  />
                  <Input
                    label="Location"
                    placeholder="Court, field, or venue"
                    value={formState.locationLabel}
                    onChange={(event) => setFormState((current) => ({ ...current, locationLabel: event.target.value }))}
                  />
                  <Input
                    label="Capacity"
                    type="number"
                    min={1}
                    placeholder="Optional"
                    value={formState.capacity}
                    onChange={(event) => setFormState((current) => ({ ...current, capacity: event.target.value }))}
                  />
                </div>
                {formError ? <p className="text-sm text-red-400">{formError}</p> : null}
                <div className="flex flex-wrap gap-3">
                  <Button type="submit" isLoading={isCreating}>
                    Create Session
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      resetForm();
                      setIsCreateOpen(false);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            ) : null}
            {data.upcomingOccurrences.length > 0 ? (
              scheduleView === 'week' ? (
                <ScheduleWeekGrid
                  occurrences={data.upcomingOccurrences}
                  canManageSchedule={canManageSchedule}
                  mode={data.mode}
                  selectedRosterOccurrenceId={selectedRosterOccurrenceId}
                  cancellingOccurrenceId={cancellingOccurrenceId}
                  onToggleRoster={(occurrenceId) =>
                    setSelectedRosterOccurrenceId((current) =>
                      current === occurrenceId ? null : occurrenceId
                    )
                  }
                  onEdit={(occurrence) => {
                    setScheduleView('list');
                    startEditingOccurrence(occurrence);
                  }}
                  onCancel={handleCancelOccurrence}
                  onOpenChat={(chatSquadId) => navigate(`/squads/${chatSquadId}`)}
                />
              ) : (
              data.upcomingOccurrences.map((occurrence) => (
                <div
                  key={occurrence.occurrenceId}
                  className="rounded-2xl border border-border bg-surface-elevated/50 p-4"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-base font-semibold text-text">{occurrence.title}</p>
                      <p className="mt-1 text-sm text-text-muted">
                        {occurrence.programName || 'Tenant session'}
                      </p>
                    </div>
                    <div className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary">
                      {occurrence.occurrenceStatus || 'Scheduled'}
                    </div>
                  </div>

                  <div className="mt-4 grid gap-2 text-sm text-text-muted">
                    <div className="flex items-center gap-2">
                      <Clock3 className="h-4 w-4 text-primary" />
                      <span>{formatDateRange(occurrence.startsAt, occurrence.endsAt)}</span>
                    </div>
                    {occurrence.locationLabel ? (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-primary" />
                        <span>{occurrence.locationLabel}</span>
                      </div>
                    ) : null}
                  {occurrence.coachName ? (
                      <div className="flex items-center gap-2">
                        <UserCircle2 className="h-4 w-4 text-primary" />
                        <span>{occurrence.coachName}</span>
                      </div>
                    ) : null}

                    {occurrence.chatSquadId ? (
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-primary" />
                        <span>{occurrence.chatSquadName || 'Linked group chat ready'}</span>
                      </div>
                    ) : null}
                  </div>

                  {editingOccurrenceId === occurrence.occurrenceId ? (
                    <form onSubmit={handleUpdateOccurrence} className="mt-4 rounded-2xl border border-border bg-surface-elevated/50 p-4 space-y-4">
                      <div className="grid gap-4 md:grid-cols-2">
                        <Input
                          label="Session Title"
                          placeholder="Optional override title"
                          value={editFormState.title}
                          onChange={(event) => setEditFormState((current) => ({ ...current, title: event.target.value }))}
                        />
                        <Input
                          label="Location"
                          placeholder="Court, field, or venue"
                          value={editFormState.locationLabel}
                          onChange={(event) => setEditFormState((current) => ({ ...current, locationLabel: event.target.value }))}
                        />
                        <Input
                          label="Starts At"
                          type="datetime-local"
                          value={editFormState.startsAt}
                          onChange={(event) => setEditFormState((current) => ({ ...current, startsAt: event.target.value }))}
                        />
                        <Input
                          label="Ends At"
                          type="datetime-local"
                          value={editFormState.endsAt}
                          onChange={(event) => setEditFormState((current) => ({ ...current, endsAt: event.target.value }))}
                        />
                        <Input
                          label="Capacity"
                          type="number"
                          min={1}
                          placeholder="Optional"
                          value={editFormState.capacity}
                          onChange={(event) => setEditFormState((current) => ({ ...current, capacity: event.target.value }))}
                        />
                      </div>
                      {editFormError ? <p className="text-sm text-red-400">{editFormError}</p> : null}
                      <div className="flex flex-wrap gap-3">
                        <Button type="submit" isLoading={isUpdating}>
                          Save Changes
                        </Button>
                        <Button type="button" variant="ghost" onClick={resetEditForm}>
                          Close
                        </Button>
                      </div>
                    </form>
                  ) : null}

                  <div className="mt-4 flex flex-wrap justify-end gap-3">
                    {canManageSchedule && data.mode === 'TENANT' ? (
                      <Button
                        variant={selectedRosterOccurrenceId === occurrence.occurrenceId ? 'secondary' : 'outline'}
                        size="sm"
                        onClick={() =>
                          setSelectedRosterOccurrenceId((current) =>
                            current === occurrence.occurrenceId ? null : occurrence.occurrenceId
                          )
                        }
                      >
                        Roster
                      </Button>
                    ) : null}
                    {canManageSchedule && data.mode === 'TENANT' && (occurrence.occurrenceStatus || '').toUpperCase() !== 'CANCELLED' ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => startEditingOccurrence(occurrence)}
                      >
                        Edit Session
                      </Button>
                    ) : null}
                    {occurrence.chatSquadId ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/squads/${occurrence.chatSquadId}`)}
                      >
                        Open Group
                      </Button>
                    ) : null}
                    {canManageSchedule && data.mode === 'TENANT' && (occurrence.occurrenceStatus || '').toUpperCase() !== 'CANCELLED' ? (
                      <Button
                        variant="outline"
                        size="sm"
                        isLoading={cancellingOccurrenceId === occurrence.occurrenceId}
                        onClick={() => handleCancelOccurrence(occurrence.occurrenceId)}
                      >
                        Cancel Session
                      </Button>
                    ) : null}
                  </div>
                  {selectedRosterOccurrenceId === occurrence.occurrenceId ? (
                    <SessionRosterPanel
                      occurrenceId={occurrence.occurrenceId}
                      canManageSchedule={canManageSchedule}
                      onClose={() => setSelectedRosterOccurrenceId(null)}
                    />
                  ) : null}
                </div>
              ))
              )
            ) : (
              data.coachDashboard ? (
                <CoachScheduleEmptySetup
                  hasPrograms={data.programs.length > 0}
                  hasStudents={linkedCoachStudents > 0}
                  onOpenCatalog={() => navigate('/classes')}
                  onCreateSession={openCreateSession}
                  onOpenAccess={() => navigate('/tenant-access')}
                  onOpenDirectory={() => navigate('/directory')}
                />
              ) : (
                <div className="rounded-2xl border border-dashed border-border bg-surface-elevated/40 p-5">
                  <p className="text-base font-semibold text-text">{data.emptyState?.title || 'No upcoming sessions yet'}</p>
                  <p className="mt-2 text-sm text-text-muted">
                    {data.emptyState?.description || 'Scheduled classes and sessions will appear here once they are created.'}
                  </p>
                </div>
              )
            )}
          </CardContent>
        </Card>

        {!data.coachDashboard ? (
          <div className="space-y-6">
            <Card variant="default">
              <CardHeader>
                <CardTitle>Coaching guidance</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-text-muted">
                <p>
                  Schedule is the official source of truth for session timing, status, and the working day.
                </p>
                <p>
                  Use Chats for reminders, cancellations, and parent or athlete communication tied to those sessions.
                </p>
                <p>
                  Schedule create, update, and cancel actions now post into the linked group automatically when that program chat exists.
                </p>
                <p>
                  Keep BUZZ for highlights, broader announcements, and promotion instead of urgent operational messaging.
                </p>
              </CardContent>
            </Card>
          </div>
        ) : null}
      </div>
    </div>
  );
}
